<template>
  <div style="position:absolute;top:0;left:0;right:0;bottom:0;" class="layout-column">
    <tiny-progress-view :visible="isLoading" text="Importiere"></tiny-progress-view>
    <div class="layout-column flex"
         style="border-radius:4px;position:relative;"
         :style="{background: compColor.contentFrame}">

      <ValidationObserver tag="div" ref="ImportDetailForm" v-slot="{}" class="layout-column flex">

        <div :style="{'z-index': '4'}" class="layout-row pa-1">
          <lx-btn small classes="mr-1 text-none"
                  @click="returnToOverview()"
                  icon="mdi-undo" :tooltip="$t('buttons.backToOverview.title')" :label="$t('COMPONENT_IMPORT.BACK_TO_OVERVIEW')"
                  width="auto"></lx-btn>
          <lx-btn small classes="mr-1 text-none"
                  @click="importPatient"
                  :disabled="disableImportButton()"
                  icon="mdi-check-bold" :tooltip="$t('COMPONENT_IMPORT.IMPORT_TO_DOCUMENTATION_TOOLTIP')" :label="$t('COMPONENT_IMPORT.IMPORT_TO_DOCUMENTATION')"
                  width="auto"></lx-btn>
          <lx-btn small classes="mr-1 text-none"
                  :disabled="!details._id || details._id==='000000000000000000000000'"
                  @click="openContextOnRowClick"
                  icon="mdi-backspace" :tooltip="$t('COMPONENT_IMPORT.DISCARD_PATIENT_TOOLTIP')" :label="$t('COMPONENT_IMPORT.DISCARD_PATIENT')"
                  width="auto"></lx-btn>
          <lx-btn small classes="mr-1" :disabled="!baseDataHasChanges"
                  @click="clearPatientForm()"
                  v-if="showBaseData"
                  icon="mdi-undo-variant" :tooltip="$t('buttons.revert_patient_changes.title')" :label="$t('buttons.revert_patient_changes.value')"
                  :hide-label="true"></lx-btn>
          <div class="flex"></div>


          <div class="layout-column layout-align-center-center mr-4">
            <div class="layout-row lx-button-group" style="max-height:35px;border-radius:5px;">
              <lx-btn :color="compColor.prime"
                      :outlined="!showBaseData"
                      :icon-color="showBaseData ? 'white' : compColor.prime"
                      small
                      iconSize="18"
                      width="auto"
                      height="35"
                      :disabled="!details._id || details._id==='000000000000000000000000'"
                      classes="text-none"
                      icon="mdi-account-circle"
                      @click="switchViews('baseData')"
                      :label="formatLabelOfBaseData()"
                      :tooltip="$t('COMPONENT_IMPORT.EDIT_BASE_DATA_OF_PATIENT_TOOLTIP')"></lx-btn>
              <lx-btn :color="compColor.prime"
                      :outlined="showBaseData"
                      :icon-color="!showBaseData ? 'white' : compColor.prime"
                      small
                      iconSize="18"
                      width="auto"
                      height="35"
                      :disabled="!details._id || details._id==='000000000000000000000000'"
                      classes="text-none"
                      icon="mdi-folder-edit"
                      @click="switchViews('diagnoses')"
                      :label="$t('COMPONENT_IMPORT.DIAGNOSES_AND_REPORT_REASONS')"
                      :tooltip="$t('COMPONENT_IMPORT.DIAGNOSES_AND_REPORT_REASONS_TOOLTIP')"></lx-btn>
            </div>
          </div>
        </div>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <lx-alert-box v-if="!details._id || details._id==='000000000000000000000000'" color="red" class="my-1 mx-1">
          {{ $t('COMPONENT_IMPORT.MESSAGE_PATIENT_NOT_EXISTS') }}
        </lx-alert-box>

        <lx-alert-box v-if="details._id && details._id !=='000000000000000000000000' && (patientDiffExists(true) || baseDataAreInvalid())" color="red" class="my-1 mx-1">
          {{ $t(details.originPatientId ? 'COMPONENT_IMPORT.MESSAGE_PATIENT_DIFF_EXISTS' : 'COMPONENT_IMPORT.MESSAGE_PATIENT_INVALID') }}
        </lx-alert-box>

        <v-divider v-if="patientDiffExists(true)" v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <ValidationObserver tag="div" :ref="'baseDataHeader'">

          <!-- patient -->

          <div class="layout-row px-1 py-2" v-if="details._id && details._id !=='000000000000000000000000'">

            <!-- patient lastname -->

            <validation-provider tag="div"
                                 name="patient.lastname"
                                 rules="required|min:2|name_string"
                                 class="flex-gt-sm-20 flex-100"
                                 v-slot="{ errors }">
              <v-text-field
                :dark="compColor.isInDarkMode"
                v-model="details.patient.baseData.lastname"
                :label="$t('COMPONENT_ACCOUNT.LASTNAME')"
                :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                dense
                outlined
                :disabled="!showBaseData"
                :error-messages="errors[0]"
                :hide-details="!errors || errors.length <= 0"
                class="mr-1 mb-0"
              ></v-text-field>
            </validation-provider>

            <!-- patient firstname -->

            <validation-provider tag="div"
                                 name="patient.firstname"
                                 rules="required|name_string"
                                 class="flex-gt-sm-20 flex-100"
                                 v-slot="{ errors }">
              <v-text-field
                :dark="compColor.isInDarkMode"
                v-model="details.patient.baseData.firstname"
                :label="$t('COMPONENT_PATIENT.FIRSTNAMES')"
                :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                :error-messages="errors[0]"
                :hide-details="!errors || errors.length <= 0"
                dense
                :disabled="!showBaseData"
                outlined
                class="mx-1 mb-0"
                height="40"
              ></v-text-field>
            </validation-provider>

            <!-- patient ais-id -->

            <validation-provider tag="div"
                                 name="patient.ais_id"
                                 :rules="{ required: true, ais_id_must_be_unique: [details.originPatientId], trim_string: true}"
                                 class="flex-gt-sm-15 flex-100"
                                 v-slot="{ errors }">
              <v-text-field
                :dark="compColor.isInDarkMode"
                v-model="details.patient.baseData.ais_id"
                :label="$t('COMPONENT_PATIENT.AIS_ID')"
                :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                :error-messages="errors[0]"
                :hide-details="!errors || errors.length <= 0"
                dense
                :disabled="!showBaseData"
                outlined
                class="mx-1 mb-0"
                height="40"
              ></v-text-field>
            </validation-provider>

            <!-- patient birthday -->

            <validation-provider tag="div"
                                 name="patient.birthday"
                                 rules="required|german_date|date_not_in_future"
                                 class="flex-gt-sm-15 flex-100"
                                 v-slot="{ errors }">
              <lx-datepicker
                :dark="compColor.isInDarkMode"
                v-model="details.patient.baseData.birthday"
                :label="$t('COMPONENT_PATIENT.BIRTHDAY')"
                :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                :error="errors[0]"
                :hide-details="!errors || errors.length <= 0"
                dense
                :disabled="!showBaseData"
                outlined
                class="mx-1 mb-0"
                height="40"
              ></lx-datepicker>
            </validation-provider>

            <!-- patient last visit -->

            <lx-datepicker
              :dark="compColor.isInDarkMode"
              v-model="details.lastVisit"
              label="Letzter Besuch"
              :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
              disabled
              hide-details
              dense
              outlined
              class="flex-gt-sm-15 flex-100 mx-1 mb-0"
              height="40"
            ></lx-datepicker>

            <!-- patient last checked in import -->

            <lx-datepicker
              :dark="compColor.isInDarkMode"
              v-model="details.lastChecked"
              label="Letzter Import"
              :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
              disabled
              hide-details
              dense
              outlined
              class="flex-gt-sm-15 flex-100 ml-1 mb-0"
              height="40"
            ></lx-datepicker>
          </div>
        </ValidationObserver>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <div class="flex pa-1" style="overflow-y: scroll;" v-show="!showBaseData">

          <lx-expansion-panels :single-open="false" v-model="panel">

            <!-- History -->

            <lx-expansion-panel v-show="details.recentForms && details.recentForms.length > 0">
              <lx-expansion-panel-header icon="mdi-history"
                                         :main-headline="$t('COMPONENT_IMPORT.HEAD_LINE_HISTORY')"
                                         :sub-headline="$t('COMPONENT_IMPORT.SUB_LINE_HISTORY')">
              </lx-expansion-panel-header>
              <lx-expansion-panel-content style="max-height: 365px; overflow-y: auto;">
                <div v-for="(form, index) in details.recentForms" :key="index" class="layout-row ma-2 elevation-4"
                     :class="[setBackgroundColor(form),{'mb-0': index < details.recentForms.length - 1}]" style="border-radius: 6px;">

                  <div v-if="!form.isRecent"
                       class="colorChild ma-1 ml-1 layout-column layout-align-start-center"
                       style="width:4px;border-radius: 4px;">
                  </div>

                  <div v-if="form.isRecent"
                       class="ma-1 ml-1 layout-column layout-align-start-center"
                       style="width:4px;border-radius: 4px;"
                       :class="['legend-color-' + getColorOfRecentForm(form)]">
                  </div>

                  <div class="flex layout-row pa-2">
                    <div class="flex layout-row layout-align-start-center">

                      <!-- Datum -->

                      <div class="pr-2">
                        <lx-datepicker
                          :label="$t('COMPONENT_IMPORT.DATE')"
                          class="mr-1 mb-0"
                          :dark="isDarkMode"
                          v-model="form.date"
                          hide-details
                          dense
                          disabled
                        ></lx-datepicker>
                      </div>

                      <!-- Meldeanlass -->

                      <div class="mr-3 mb-2 flex-gt-sm-20">
                        <fake-input-frame :label="$t('COMPONENT_IMPORT.REPORT_TYPE')"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ setReportReasonInHistory(form) }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- ICD-10 Code alle Formulare außer Diagnose-->

                      <div class="mr-3 mb-2 flex-gt-sm-15" v-if="form.formType !== 'register' && form.formType !== 'diagnosis'">
                        <fake-input-frame :label="$t('COMPONENT_IMPORT.DIAGNOSIS')"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ getDiagnosisForHistory(form) }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- Diagnose ICD-10-Code -->

                      <div class="flex-gt-sm-15" v-if="form && form.icd10 && form.icd10.code && (form.formType === 'register' || form.formType === 'diagnosis')">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                          :label="$t('COMPONENT_IMPORT.ICD_10')"
                          hide-details
                          disabled
                          outlined
                          class="mb-0"
                          v-model="form.icd10.code"
                          dense
                        ></v-text-field>
                      </div>

                      <!-- ------------------------ -->
                      <!--      Sonderfelder        -->
                      <!-- ------------------------ -->

                      <!-- Diagnose (Bestandsdiagnose) - Diagnosesicherung, Seitenlokalisation -->

<!--                      <div class="ml-3 mb-2 flex" v-if="form.formType === 'register'">-->
<!--                        <fake-input-frame :label="$t('FORMS_DIVERSE.ICD_CODE_DIAGNOSTIC_ASSURANCE')"-->
<!--                                          disabled-->
<!--                                          style="margin-top:-2px;width:100%"-->
<!--                                          height="50px">-->
<!--                          <span style="font-size: 0.9em;padding-left: 14px;"-->
<!--                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">-->
<!--                            {{ getFieldValue(form.diagnosticAssurances, 'DiagnosesicherungTypes').text }}-->
<!--                          </span>-->
<!--                        </fake-input-frame>-->
<!--                      </div>-->

                      <div class="mx-3 mb-2 flex-gt-sm-15" v-if="form.formType === 'register'">
                        <fake-input-frame :label="$t('FORMS_DIVERSE.ICD_CODE_SIDE_LOCALIZATION')"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ getFieldValue(form.localization, 'SeitenlokalisationTypesShort') }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- Diagnose (Import) - Diagnosesicherung, Seitenlokalisation -->

<!--                      <div class="ml-3 mb-2 flex" v-if="form.formType === 'diagnosis'">-->
<!--                        <fake-input-frame :label="$t('FORMS_DIVERSE.ICD_CODE_DIAGNOSTIC_ASSURANCE')"-->
<!--                                          disabled-->
<!--                                          style="margin-top:-2px;"-->
<!--                                          height="50px">-->
<!--                          <span style="font-size: 0.9em;padding-left: 14px;"-->
<!--                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">-->
<!--                            {{ getFieldValue(form.additionalFields.register.diagnosesicherung, 'DiagnosesicherungTypes').text }}-->
<!--                          </span>-->
<!--                        </fake-input-frame>-->
<!--                      </div>-->

                      <div class="mx-3 mb-2 flex-gt-sm-15" v-if="form.formType === 'diagnosis'">
                        <fake-input-frame :label="$t('FORMS_DIVERSE.ICD_CODE_SIDE_LOCALIZATION')"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ getFieldValue(form.additionalFields.register.seitenlokalisation, 'SeitenlokalisationTypesShort') }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- Verlauf - Gesamtbeurteilung-->

                      <div class="mr-3 mb-2 flex" v-if="form.formType === 'progress'">
                        <fake-input-frame :label="$t('COMPONENT_IMPORT.OVERALL_ASSESSMENT_TUMOR')"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ getEvaluationForProgress(form) }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- syst. Therapie - Wirkstoffe, keine vorhanden (fallback) -->

                      <div class="ml-3 mb-2 flex" v-if="form.formType === 'systemicTherapy' && (!form.agents || form.agents.length === 0) && !form.additionalFields.systemicTherapy.syst_no_agent">
                        <fake-input-frame :label="$t('COMPONENT_IMPORT.AGENTS')"
                                          v-if="(!form.agents || form.agents.length === 0) && !form.additionalFields.systemicTherapy.syst_no_agent"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ $t('COMPONENT_IMPORT.NO_AGENTS') }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- syst. Therapie - Wirkstoffe -->

                      <v-select
                        v-if="form.formType === 'systemicTherapy' && form.agents && form.agents.length > 0 && !form.additionalFields.systemicTherapy.syst_no_agent"
                        :color="compColor.primeColor"
                        :label="$t('COMPONENT_IMPORT.AGENTS')"
                        v-model="form.agents"
                        :items="getAgentList(form.agents)"
                        hide-details
                        disabled
                        outlined
                        class="mr-3 mb-0 flex"
                        :dark="compColor.isInDarkMode"
                        :menu-props="{ bottom: true, offsetY: true }"
                        dense>
                      </v-select>

                      <!-- syst. Therapie - Therapieprotokoll, wenn Wirkstoffe -->

                      <v-text-field
                        :dark="compColor.isInDarkMode"
                        v-if="form.formType === 'systemicTherapy' && !form.additionalFields.systemicTherapy.syst_no_agent && form.additionalFields.systemicTherapy.syst_protokoll"
                        :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                        :label="$t('COMPONENT_IMPORT.PROTOCOL')"
                        hide-details
                        disabled
                        outlined
                        class="mr-3 mb-0 flex-gt-sm-10"
                        v-model="form.additionalFields.systemicTherapy.syst_protokoll"
                        dense
                      ></v-text-field>

                      <!-- syst. Therapie - Therapieart, wenn Checkbox "keine Wirkstoffe" ausgewählt -->

                      <div class="mr-3 mb-2 flex" v-if="form.formType === 'systemicTherapy' && form.additionalFields.systemicTherapy.syst_no_agent">
                        <fake-input-frame :label="$t('COMPONENT_IMPORT.KIND_OF_THERAPY')"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ getKindOfSystTherapyAsString(form) }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- syst. Therapie - Therapiebeginn, wenn Behandlungsende -->

                      <lx-datepicker
                        v-if="form.formType === 'systemicTherapy' && form.reportType === 'behandlungsende'"
                        :color="compColor.textInput"
                        :label="$t('FORMS_DIVERSE.MODULE_SYST_THERAPY_START_DATE_LONG')"
                        hide-details
                        disabled
                        outlined
                        dense
                        class="mr-3 mb-0 flex-gt-sm-10"
                        v-model="form.additionalFields.systemicTherapy.syst_beginn_datum"
                      ></lx-datepicker>

                      <!-- syst. Therapie  - Grund Ende, wenn Behandlungsende -->

                      <v-select
                        v-if="form.formType === 'systemicTherapy' && form.reportType === 'behandlungsende'"
                        :color="compColor.primeColor"
                        :label="$t('FORMS_DIVERSE.MODULE_SYST_REASON_FOR_END')"
                        v-model="form.additionalFields.systemicTherapy.syst_ende_grund"
                        :items="SYST_Ende_GrundTypes"
                        hide-details
                        disabled
                        outlined
                        dense
                        class="mr-3 mb-0 flex-gt-sm-10"
                        :menu-props="{ bottom: true, offsetY: true }"
                        :dark="compColor.isInDarkMode">
                      </v-select>

                      <!-- Strahlentherapie - Zielgebiet -->

                      <div class="mr-3 mb-2 flex" v-if="form.formType === 'radiationTherapy'">
                        <fake-input-frame :label="$t('COMPONENT_IMPORT.RADIATION_DESTINATION')"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ getDestinationOfRadiation(form) }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- Operationen - OPS-->

                      <div class="mr-3 mb-2 flex" v-if="form.formType === 'operation'">
                        <fake-input-frame :label="$t('COMPONENT_IMPORT.OPERATION_OPS')"
                                          disabled
                                          style="margin-top:-2px;"
                                          height="50px">
                          <span style="font-size: 0.9em;padding-left: 14px;"
                                :style="{ color: compColor.isInDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)'}">
                            {{ getShortTextOfOperation(form) }}
                          </span>
                        </fake-input-frame>
                      </div>

                      <!-- Tumorkonferenz - Typ -->

                      <div class="mr-3 flex" v-if="form.formType === 'tumorConference'">
                        <v-select
                          :color="compColor.primeColor"
                          :label="$t('FORMS_DIVERSE.MODULE_TUMOR_CONFERENCE_TYPE')"
                          v-model="form.additionalFields.tumorConference.typ"
                          :items="Tumorkonferenz_TypTypes"
                          hide-details
                          disabled
                          outlined
                          class="mb-0"
                          :dark="compColor.isInDarkMode"
                          :menu-props="{ bottom: true, offsetY: true }"
                          dense>
                        </v-select>
                      </div>
                    </div>

                    <!-- Buttons -->

                    <v-divider vertical class="mx-1"></v-divider>

                    <div class="layout-row layout-align-center-center" style="width:40px;" v-if="!form.isRecent && !form.isAdditionalForm">
                      <v-tooltip bottom open-delay="350">
                        <template v-slot:activator="{ on }">
                          <v-btn v-on="on" text icon :color="isDarkMode ? 'grey--text' : 'grey--text'"
                                 :dark="compColor.isInDarkMode"
                                 :disabled="isDiagnosisAssigned(form)"
                                 @click="changeFormStatus(form, 'undo')">
                            <v-icon small>
                              mdi-undo-variant
                            </v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('COMPONENT_IMPORT.BTN_EDIT_AGAIN') }}</span>
                      </v-tooltip>
                    </div>

                    <div class="layout-row layout-align-center-center" style="width:40px;" v-else-if="form.isAdditionalForm">
                      <v-tooltip bottom open-delay="350">
                        <template v-slot:activator="{ on }">
                          <v-btn v-on="on" text icon :color="isDarkMode ? 'grey--text' : 'grey--text'"
                                 :dark="compColor.isInDarkMode"
                                 @click="changeFormStatus(form, 'undo')">
                            <v-icon small>
                              delete_forever
                            </v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('COMPONENT_IMPORT.BTN_DELETE') }}</span>
                      </v-tooltip>
                    </div>

                    <div style="width: 40px;" v-else></div>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>

            <!-- Mögliche Diagnosen, welche im Import gefunden wurden -->

            <lx-expansion-panel :disabled="true" v-show="filterDiagnoses(details.diagnoses) && filterDiagnoses(details.diagnoses).length > 0">
              <lx-expansion-panel-header icon="mdi-needle"
                                         :disabled="true"
                                         :main-headline="$t('COMPONENT_IMPORT.HEADER_DIAGNOSES')"
                                         :sub-headline="$t('COMPONENT_IMPORT.HEADER_DIAGNOSES_SUB_LINE')">
              </lx-expansion-panel-header>
              <lx-expansion-panel-content class="pt-3"
                                          style="border-radius:0 0 5px 5px;">
                <div v-for="(diagnosis, index) in filterDiagnoses(details.diagnoses)"
                     :key="index"
                     style="border-radius: 6px;"
                     class="layout-row flex mx-2 mt-2 mb-4 elevation-4 ">

                  <div class="flex layout-row px-3">
                    <ValidationObserver tag="div" :ref="'diagnoses.' + index + '.header'" class="layout-row flex pt-3">
                      <div class="flex layout-row">

                        <!-- Diagnosedatum -->

                        <div class="pr-4 flex-gt-sm-15">
                          <validation-provider tag="div"
                                               :name="'diagnoses.' + index + '.date'"
                                               :rules="{required:true,german_date:true,date_not_in_future:true,date_must_be_lesser_than_death:[details.dateOfDeath,diagnosis.additionalFields.register.diagnosedatum_genauigkeit]}"
                                               v-slot="{ errors }">
                            <lx-datepicker
                              :error="errors[0]"
                              @input="setIcdVersionAfterDate(diagnosis)"
                              :label="$t('COMPONENT_IMPORT.DATE') + ' *'"
                              class="mr-1 mb-0"
                              :hideDetails="!errors || errors && errors.length <= 0"
                              v-model="diagnosis.date"
                              dense
                            ></lx-datepicker>
                          </validation-provider>
                        </div>

                        <!-- Primärtumor Datumsgenauigkeit Diagnosedatum -->

                        <div class="flex-gt-sm-20 layout-row mr-4">
                          <validation-provider tag="div"
                                               class="flex"
                                               :name="'diagnoses.' + index + '.additionalFields.register.diagnosedatum_genauigkeit'"
                                               :ref="'diagnoses.' + index + '.additionalFields.register.diagnosedatum_genauigkeit'"
                                               rules="required"
                                               v-slot="{ errors }">
                            <v-select
                              :color="compColor.primeColor"
                              :label="$t('FORMS_DIVERSE.ICD_CODE_DATE_ACCURACY') + ' *'"
                              v-model="diagnosis.additionalFields.register.diagnosedatum_genauigkeit"
                              :items="DateAccuracyLongTypes"
                              outlined
                              :hide-details="!errors || errors.length <= 0"
                              dense
                              :error-messages="errors[0]"
                              :menu-props="{ bottom: true, offsetY: true }"
                              :dark="compColor.isInDarkMode">
                              <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-on="on"
                                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                  <v-list-item-content class="py-0">
                                    <label style="cursor:pointer;"
                                           :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                           class="lighten-4 text--darken-2">{{ item.text }}</label>
                                  </v-list-item-content>
                                </v-list-item>
                              </template>
                            </v-select>
                          </validation-provider>

                          <v-tooltip bottom open-delay="1000">
                            <template v-slot:activator="{ on }">
                              <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-start pt-2 ml-1"
                                   style="cursor: pointer;">
                                <v-icon :color="compColor.prime" medium>info</v-icon>
                              </div>
                            </template>
                            <p style="max-width: 400px;" class="my-1">{{ $t('FORMS_DIVERSE.TOOLTIP_DATE_ACCURACY') }}</p>
                          </v-tooltip>
                        </div>

                        <!-- ICD-10-Code -->

                        <div class="pr-2 flex-gt-sm-10">
                          <validation-provider tag="div"
                                               :name="'diagnoses.' + index + '.icd10.code'"
                                               :ref="'diagnoses.' + index + '.icd10.code'"
                                               rules="required|icd10_code_auto_complete"
                                               v-slot="{ errors }">
                            <lx-autocomplete
                                :label="$t('FORMS_DIVERSE.ICD_CODE') + ' *'"
                                v-model="diagnosis.icd10.code"
                                :error="errors[0]"
                                :setValueOnlyPerChoice="true"
                                @search-method="getICD10ForAutocomplete($event, diagnosis, index)"
                                @input="setDiagnosis($event, diagnosis, index)"
                                dense
                                :hideDetails="!errors || errors && errors.length <= 0"
                                :index="index"
                                :results="resultSearchModel">
                              <template v-slot="slotProps">
                                <div class="layout-row">
                                  <div style="width: 100px;border-radius: 4px;"
                                       class="mr-2 px-2 white lighten-2 layout-column layout-align-center-center">
                                    <h3>
                                      <lx-text-highlight :text="slotProps.lineItem.code"
                                                         :search-string="slotProps.searchText"
                                                         :color="compColor.prime"></lx-text-highlight>
                                    </h3>
                                  </div>
                                  <div class="flex">
                                    <span class="body-2">
                                      <lx-text-highlight :text="slotProps.lineItem.description" :search-string="slotProps.searchText"
                                                         :color="compColor.prime"></lx-text-highlight>
                                    </span>
                                  </div>
                                </div>
                              </template>
                            </lx-autocomplete>
                          </validation-provider>
                        </div>

                        <div class="px-2 flex-gt-sm-15">
                          <validation-provider tag="div"
                                               :name="'diagnoses.' + index + '.icd10.version'"
                                               :ref="'diagnoses.' + index + '.icd10.version'"
                                               rules="required"
                                               v-slot="{ errors }">
                            <v-select
                                :color="compColor.primeColor"
                                :label="$t('FORMS_DIVERSE.ICD_CODE_VERSION') + ' *'"
                                v-model="diagnosis.icd10.version"
                                :items="Primaertumor_ICD_VersionTypes"
                                @change="resetICD10Code(diagnosis.icd10)"
                                outlined
                                dense
                                :hide-details="errors.length === 0"
                                :error-messages="errors[0]"
                                :menu-props="{ bottom: true, offsetY: true }"
                                :dark="compColor.isInDarkMode">
                              <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-on="on"
                                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                  <v-list-item-content class="py-0">
                                    <label style="cursor:pointer;"
                                           :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                           class="lighten-4 text--darken-2">{{ item.text }}</label>
                                  </v-list-item-content>
                                </v-list-item>
                              </template>
                            </v-select>
                          </validation-provider>
                        </div>

                        <!-- Seitenlokalisation paariger Tumor -->

                        <div class="px-2 flex-gt-sm-10 layout-row" v-if="diagnosis.isPairedTumor">
                          <validation-provider tag="div"
                                               class="flex-100"
                                               :name="'diagnoses.' + index + '.additionalFields.register.seitenlokalisation'"
                                               rules="required">
                            <v-select
                              :dark="compColor.isInDarkMode"
                              :color="compColor.primeColor"
                              :items="sideLocalizationsPairedTumor"
                              :label="$t('COMPONENT_IMPORT.LOCALIZATION_PAIRED_TUMOR') + ' *'"
                              hide-details
                              outlined
                              class="mb-0"
                              v-model="diagnosis.additionalFields.register.seitenlokalisation"
                              :menu-props="{ bottom: true, offsetY: true }"
                              dense>
                              <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-on="on"
                                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                  <v-list-item-content class="py-0">
                                    <label style="cursor:pointer;"
                                           :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                           class="lighten-4 text--darken-2">{{ item.text }}</label>
                                  </v-list-item-content>
                                </v-list-item>
                              </template>
                            </v-select>
                          </validation-provider>

                          <v-tooltip bottom open-delay="1000" max-width="800">
                            <template v-slot:activator="{ on }">
                              <div v-on="on" class="layout-row layout-align-start-start ml-1 mt-2"
                                   style="cursor: pointer;">
                                <v-icon :color="compColor.prime" medium>info</v-icon>
                              </div>
                            </template>

                            <p style="text-align: justify;" class="my-1">{{$t('COMPONENT_IMPORT.TOOLTIP_SIDE_LOCALIZATION_PAIRED_TUMOR')}}</p>
                          </v-tooltip>
                        </div>

                        <!-- Seitenlokalisation nicht paariger Tumor -->

                        <div class="px-2 flex-gt-sm-10" v-if="!diagnosis.isPairedTumor">
                          <validation-provider tag="div"
                                               :name="'diagnoses.' + index + '.additionalFields.register.seitenlokalisation'"
                                               rules="">
                            <v-select
                              :dark="compColor.isInDarkMode"
                              :color="compColor.primeColor"
                              :items="sideLocalizations"
                              :label="$t('FORMS_DIVERSE.ICD_CODE_SIDE_LOCALIZATION')"
                              hide-details
                              outlined
                              class="mb-0"
                              v-model="diagnosis.additionalFields.register.seitenlokalisation"
                              :menu-props="{ bottom: true, offsetY: true }"
                              dense>
                              <template v-slot:selection='{ item }'>
                                <span v-if="!compColor.isInDarkMode" style="font-size: 0.9em;padding-top: 1px;" class="label_no_wrap"
                                      :style="{ 'color': '#445a64'}">{{ item.text }}</span>
                                <span v-if="compColor.isInDarkMode" style="font-size: 0.9em;padding-top: 1px;" class="label_no_wrap"
                                      :style="{ 'color': 'white'}">{{ item.text }}</span>
                              </template>
                              <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-on="on"
                                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                  <v-list-item-content class="py-0">
                                    <label style="cursor:pointer;"
                                           :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                           class="lighten-4 text--darken-2">{{ item.text }}</label>
                                  </v-list-item-content>
                                </v-list-item>
                              </template>
                            </v-select>
                          </validation-provider>
                        </div>

                        <!-- Melder bei BAG -->

                        <div class="px-2 flex-gt-sm-15">
                          <validation-provider tag="div"
                                               :name="'diagnoses.' + index + '.reporter'"
                                               rules="required">
                            <v-select
                              :color="compColor.textInput"
                              :dark="compColor.isInDarkMode"
                              :items="physicianList"
                              outlined
                              hide-details
                              dense
                              class="mb-0"
                              :value-comparator="compareReporterInList"
                              :label="$t('COMPONENT_CANCER_MESSAGE_DETAILS.CARE_PROVIDER') + ' *'"
                              v-if="isMedicalCommunity"
                              v-model="diagnosis.reporter"
                              :menu-props="{ bottom: true, offsetY: true }">
                              <template v-slot:selection='{ item }'>
                                <span v-if="!compColor.isInDarkMode" class="label_no_wrap"
                                      :style="{ 'color': '#445a64'}">
                                  {{ item.value.name }}</span>
                                <span v-if="compColor.isInDarkMode" class="label_no_wrap"
                                      :style="{ 'color': 'white'}">{{ item.value.name }}</span>
                              </template>
                              <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-on="on"
                                             :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                  <v-list-item-content class="py-0 layout-row">
                                    <div  class="py-0 layout-row">
                                      <lx-text-highlight :text="item.value.name" :search-string="item.value.name"
                                                         :color="compColor.prime"></lx-text-highlight>
                                      <span v-if="item.value.lanr" class="ml-2" :style="{ color: compColor.isInDarkMode ? '#546E7A' : 'rgba(0, 0, 0, 0.7)'}">(LANR: {{ item.value.lanr }} | Melder-ID: {{ item.value.melderId }})</span>
                                      <span v-if="item.value.iknr" class="ml-2" :style="{ color: compColor.isInDarkMode ? '#546E7A' : 'rgba(0, 0, 0, 0.7)'}">(IKNR: {{ item.value.iknr }} | Melder-ID: {{ item.value.melderId }})</span>
                                    </div>
                                  </v-list-item-content>
                                </v-list-item>
                              </template>
                            </v-select>
                          </validation-provider>
                        </div>

                        <div class="flex"></div>

                        <div class="pb-4">
                          <v-divider vertical class="mx-2 pb-4"></v-divider>
                        </div>

                        <!-- Buttons -->

                        <div style="margin-top: -13px !important;" class="layout-row layout-align-center-center mt-0 pt-0">

                          <!-- Undo -->

                          <div @keydown.enter="undoForm(diagnosis, 'diagnoses', index)">
                            <lx-btn small classes="mr-1 text-none"
                                    :disabled="diagnosisIsPristine('diagnoses.' + index)"
                                    as-icon
                                    as-text
                                    icon-size="18"
                                    @click="undoForm(diagnosis, 'diagnoses', index)"
                                    icon="mdi-undo-variant" :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_RESET_CHANGES')"
                                    width="35" height="35"></lx-btn>
                          </div>

                          <!-- Import -->

                          <div @keydown.enter="changeFormStatus(diagnosis, 'import')">
                            <lx-btn small classes="mr-1 text-none"
                                    :disabled="formIsInValid('diagnoses.' + index)"
                                    as-icon
                                    as-text
                                    icon-size="18"
                                    :icon-color="btnColorSelected"
                                    @click="changeFormStatus(diagnosis, 'import')"
                                    icon="mdi-check-circle"
                                    :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_DIAGNOSIS_TO_HISTORY_REPORT')"
                                    width="35" height="35"></lx-btn>
                          </div>

                          <!-- Ignore -->

                          <div @keydown.enter="changeFormStatus(diagnosis, 'ignore')">
                            <lx-btn small classes="mr-1 text-none"
                                    :disabled="formIsInValid('diagnoses.' + index)"
                                    as-icon
                                    as-text
                                    icon-size="18"
                                    :icon-color="btnColorIgnored"
                                    @click="changeFormStatus(diagnosis, 'ignore')"
                                    icon="no_encryption"
                                    :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_DIAGNOSIS_TO_HISTORY_IGNORE')"
                                    width="35" height="35"></lx-btn>
                          </div>

                          <!-- Delete -->

                          <div @keydown.enter="changeFormStatus(diagnosis, 'delete')">
                            <lx-btn small classes="mr-1 text-none"
                                    as-icon
                                    as-text
                                    icon-size="18"
                                    @click="changeFormStatus(diagnosis, 'delete')"
                                    icon="delete_forever" :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_DELETE')"
                                    width="35" height="35"></lx-btn>
                          </div>
                        </div>
                      </div>
                    </ValidationObserver>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>

            <!-- Mögliche Meldeanlässe, welche im Import gefunden wurden außer Diagnosen -->

            <lx-expansion-panel :disabled="true" v-if="details._id && details._id !=='000000000000000000000000'">
              <lx-expansion-panel-header icon="mdi-clipboard-edit-outline"
                                         :disabled="true"
                                         :main-headline="$t('COMPONENT_IMPORT.HEADER_REPORT_REASONS')"
                                         :sub-headline="$t('COMPONENT_IMPORT.HEADER_REPORT_REASONS_SUB_LINE')">
              </lx-expansion-panel-header>
              <lx-expansion-panel-content class="pt-2"
                                          style="border-radius:0 0 5px 5px;">
                <lx-expansion-panels accordion multiple v-model="formsPanel">
                  <lx-expansion-panel v-for="(form, index) in details.forms" :key="index" class="mx-2" :tooltip-text-open-icon="$t('COMPONENT_IMPORT.TOOLTIP_EXPAND_PANEL')">
                    <lx-expansion-panel-header-with-slot style="border-radius: 6px;"
                                                         v-if="!form.isAdditionalForm">
                      <div class="flex layout-row">
                        <ValidationObserver tag="div" :ref="'forms.' + index + '.header'" class="layout-row flex">
                          <div class="flex layout-row px-3 pt-3 pb-4">

                            <!-- Datum -->

                            <div class="pr-2 flex-gt-sm-15" v-if="form.formType !== 'systemicTherapy'">
                              <validation-provider tag="div"
                                                   :name="'forms.' + index + '.date'"
                                                   :ref="'forms.' + index + '.date'"
                                                   :rules="returnRulesForDateValues(form)"
                                                   v-slot="{ errors }">
                                <lx-datepicker
                                  :error="errors[0]"
                                  :label="$t('COMPONENT_IMPORT.DATE') + ' *'"
                                  class="mr-1 mb-0"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  v-model="form.date"
                                  dense
                                ></lx-datepicker>
                              </validation-provider>
                            </div>

                            <div class="pr-2 flex-gt-sm-15 layout-row" v-if="form.formType === 'systemicTherapy' && !form.dateIsSelected && form.datesList && form.datesList.length > 1">
                              <validation-provider tag="div"
                                                   class="flex"
                                                   :name="'forms.' + index + '.date'"
                                                   :ref="'forms.' + index + '.date'"
                                                   :rules="returnRulesForDateValues(form)"
                                                   v-slot="{ errors }">
                                <v-select
                                  :error-messages="errors[0]"
                                  :label="$t('COMPONENT_IMPORT.DATE') + ' *'"
                                  class="mr-1 mb-0"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  :color="compColor.primeColor"
                                  :dark="compColor.isInDarkMode"
                                  v-model="form.date"
                                  outlined
                                  @change="setUUIDOfSystemicTherapy(form)"
                                  :items="form.datesList"
                                  item-value="date"
                                  dense
                                  :menu-props="{ bottom: true, offsetY: true }">
                                  <template v-slot:selection='{ item }'>
                                    <label style="font-size: 14.4px !important;">
                                      {{ formatDateOfSystemicTherapy(item) }}
                                    </label>
                                  </template>
                                  <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on"
                                                 :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                      <v-list-item-content class="py-0">
                                        <label style="cursor:pointer;"
                                               :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                               class="lighten-4 text--darken-2">{{ formatDateOfSystemicTherapy(item) }}</label>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </template>
                                </v-select>
                              </validation-provider>

                              <lx-btn small classes="mt-1 text-none"
                                      as-icon
                                      as-text
                                      icon-size="18"
                                      @click="switchDateStateOfSystemicTherapy(form, true)"
                                      icon="mdi-calendar-edit"
                                      :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_SWITCH_TO_MANUALLY_DATE')"
                                      width="35" height="35"></lx-btn>
                            </div>

                            <div class="pr-2 flex-gt-sm-15 layout-row" v-if="form.formType === 'systemicTherapy' && (!form.datesList || form.dateIsSelected || (form.datesList && form.datesList.length === 1))">
                              <validation-provider tag="div"
                                                   class="flex"
                                                   :name="'forms.' + index + '.date'"
                                                   :ref="'forms.' + index + '.date'"
                                                   :rules="returnRulesForDateValues(form)"
                                                   v-slot="{ errors }">
                                <lx-datepicker
                                  :error="errors[0]"
                                  :label="$t('COMPONENT_IMPORT.DATE') + ' *'"
                                  class="mr-1 mb-0"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  v-model="form.date"
                                  dense
                                ></lx-datepicker>
                              </validation-provider>

                              <lx-btn small classes="mt-1 text-none"
                                      as-icon
                                      as-text
                                      :disabled="!form.datesList || form.datesList.length === 1"
                                      icon-size="18"
                                      @click="switchDateStateOfSystemicTherapy(form, false)"
                                      icon="mdi-format-list-bulleted"
                                      :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_SWITCH_TO_LIST_DATE')"
                                      width="35" height="35"></lx-btn>
                            </div>

                            <!-- Meldeanlass -->

                            <div class="px-2 flex-gt-sm-20">
                              <validation-provider tag="div"
                                                   :name="'forms.' + index + '.reportType'"
                                                   v-slot="{ errors }">
                                <v-select
                                  :color="compColor.primeColor"
                                  :label="$t('COMPONENT_IMPORT.REPORT_TYPE')"
                                  v-model="form.reportDisplayName"
                                  :items="reportTypesImportedForms[form.formType]"
                                  @change="setFormValues(form)"
                                  outlined
                                  :disabled="form.formType !== 'systemicTherapy'"
                                  class="mb-0"
                                  :error-messages="errors[0]"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  :dark="compColor.isInDarkMode"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  dense>
                                  <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on"
                                                 :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                      <v-list-item-content class="py-0">
                                        <label style="cursor:pointer;"
                                               :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                               class="lighten-4 text--darken-2">{{ item.text }}</label>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </template>
                                </v-select>
                              </validation-provider>
                            </div>

                            <!-- ICD-10-Code Primärtumor -->

                            <div class="px-2 flex-gt-sm-15">
                              <validation-provider tag="div"
                                                   :name="'forms.' + index + '.diagnosis'"
                                                   rules="required"
                                                   v-slot="{ errors }">
                                <v-select
                                  :color="compColor.primeColor"
                                  :label="$t('COMPONENT_IMPORT.DIAGNOSIS') + ' *'"
                                  v-model="form.diagnosis"
                                  :items="details.diagnoses"
                                  :item-text="formatDiagnosisText"
                                  @change="validateDateAfterDiagnoseSelection('forms.' + index + '.date', form)"
                                  item-value="uuid"
                                  outlined
                                  class="mb-0"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  :error-messages="errors[0]"
                                  :dark="compColor.isInDarkMode"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  dense>
                                  <template v-slot:selection='{ item }'>
                                    <label style="font-size: 14.4px !important;" class="label_no_wrap">
                                      {{ formatDiagnosisText(item) }}
                                    </label>
                                  </template>
                                  <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on"
                                                 :style="{ 'cursor': attrs.disabled ? 'default !important' : 'pointer' }"
                                                 :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                      <v-list-item-content class="py-0">
                                        <label :style="{ 'cursor': attrs.disabled ? 'default !important' : 'pointer' }"
                                               :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? [attrs.disabled ? 'grey--text' : 'white--text'] : [attrs.disabled ? 'grey--text text--lighten-2' : '']"
                                               class="lighten-4">{{ formatDiagnosisText(item) }}</label>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </template>
                                </v-select>
                              </validation-provider>
                            </div>

                            <!-- Wirkstoffe syst. Therapie -->

                            <div class="px-2 flex-gt-sm-20" v-if="form.formType === 'systemicTherapy'">
                              <v-select
                                :color="compColor.primeColor"
                                :label="$t('COMPONENT_IMPORT.AGENTS')"
                                v-model="form.agents"
                                :items="getAgentList(form.agents)"
                                outlined
                                class="mb-0"
                                disabled
                                hideDetails
                                :dark="compColor.isInDarkMode"
                                :menu-props="{ bottom: true, offsetY: true }"
                                dense>
                                <template v-slot:item="{ item, on, attrs }">
                                  <v-list-item v-on="on"
                                               :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                    <v-list-item-content class="py-0">
                                      <label style="cursor:pointer;"
                                             :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                             class="lighten-4 text--darken-2">{{ item.text }}</label>
                                    </v-list-item-content>
                                  </v-list-item>
                                </template>
                              </v-select>
                            </div>

                            <!-- ICD-10 Code Fernmetastase Verlauf -->

                            <div class="px-2 flex-gt-sm-20" v-if="form.reportDisplayName === 'statusaenderung_metastase'">
                              <v-text-field
                                :color="compColor.primeColor"
                                :dark="compColor.isInDarkMode"
                                :label="$t('COMPONENT_IMPORT.METASTASIS')"
                                hide-details
                                disabled
                                outlined
                                class="mb-0"
                                dense
                                :value="formatMetastasisText({code: form.icd10.code, localization: form.metastasisLocalization})"
                              ></v-text-field>
                            </div>

                            <div class="px-2 flex-gt-sm-20" v-if="form.formType !== 'systemicTherapy' && form.reportDisplayName !== 'statusaenderung_metastase'"></div>

                            <!-- Melder bei BAG -->

                            <div class="px-2 flex-gt-sm-15">
                              <validation-provider tag="div"
                                                   :name="'forms.' + index + '.reporter'"
                                                   rules="required"
                                                   v-slot="{ errors }">
                                <v-select
                                  :color="compColor.primeColor"
                                  :dark="compColor.isInDarkMode"
                                  :items="physicianList"
                                  outlined
                                  dense
                                  class="mb-0"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  :error-messages="errors[0]"
                                  :label="$t('COMPONENT_CANCER_MESSAGE_DETAILS.CARE_PROVIDER') + ' *'"
                                  v-if="isMedicalCommunity"
                                  v-model="form.reporter"
                                  :value-comparator="compareReporterInList"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                >
                                  <template v-slot:selection='{ item }'>
                                    <span v-if="!compColor.isInDarkMode" class="label_no_wrap"
                                          :style="{ 'color': '#445a64'}">
                                      {{ item.value.name }}</span>
                                    <span v-if="compColor.isInDarkMode" class="label_no_wrap"
                                          :style="{ 'color': 'white'}">
                                      {{ item.value.name }}</span>
                                  </template>
                                  <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on"
                                                 :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                      <v-list-item-content class="py-0 layout-row">
                                        <div  class="py-0 layout-row">
                                          <lx-text-highlight :text="item.value.name" :search-string="item.value.name"
                                                             :color="compColor.prime"></lx-text-highlight>
                                          <span v-if="item.value.lanr" class="ml-2" :style="{ color: compColor.isInDarkMode ? '#546E7A' : 'rgba(0, 0, 0, 0.7)'}">(LANR: {{ item.value.lanr }} | Melder-ID: {{ item.value.melderId }})</span>
                                          <span v-if="item.value.iknr" class="ml-2" :style="{ color: compColor.isInDarkMode ? '#546E7A' : 'rgba(0, 0, 0, 0.7)'}">(IKNR: {{ item.value.iknr }} | Melder-ID: {{ item.value.melderId }})</span>
                                        </div>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </template>
                                </v-select>
                              </validation-provider>
                            </div>

                            <div class="flex"></div>

                            <v-divider vertical class="mx-2"></v-divider>

                            <!-- Buttons -->

                            <div class="layout-row layout-align-center-center">
                              <div @keydown.enter="undoForm(form, 'forms', index)">
                                <lx-btn small classes="mr-1 text-none"
                                        as-icon
                                        as-text
                                        icon-size="18"
                                        :disabled="formIsPristine(index)"
                                        @click="undoForm(form, 'forms', index)"
                                        icon="mdi-undo-variant"
                                        :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_RESET_CHANGES')"
                                        width="35" height="35"></lx-btn>
                              </div>

                              <div @keydown.enter="changeFormStatus(form, 'complete', false, index)">
                                <lx-btn small classes="mr-1 text-none"
                                        :disabled="formIsInValid('forms.' + index)"
                                        as-icon
                                        :icon-color="btnColorCompleted"
                                        as-text
                                        icon-size="20"
                                        @click="changeFormStatus(form, 'complete', false, index)"
                                        icon="mdi-briefcase-check"
                                        :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_REPORT_REASON_TO_HISTORY_COMPLETE')"
                                        width="35" height="35"></lx-btn>
                              </div>

                              <div @keydown.enter="changeFormStatus(form, 'import', false, index)">
                                <lx-btn small classes="mr-1 text-none"
                                        :disabled="formHeaderIsInValid('forms.' + index)"
                                        as-icon
                                        as-text
                                        tabindex="0"
                                        :icon-color="btnColorSelected"
                                        icon-size="18"
                                        @click="changeFormStatus(form, 'import', false, index)"
                                        icon="mdi-check-circle"
                                        :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_REPORT_REASON_TO_HISTORY')"
                                        width="35" height="35"></lx-btn>
                              </div>

                              <div @keydown.enter="changeFormStatus(form, 'ignore', false, index)">
                                <lx-btn small classes="mr-1 text-none"
                                        :disabled="formHeaderIsInValid('forms.' + index)"
                                        as-icon
                                        as-text
                                        icon-size="18"
                                        :icon-color="btnColorIgnored"
                                        @click="changeFormStatus(form, 'ignore', false, index)"
                                        icon="no_encryption"
                                        :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_REPORT_REASON_TO_HISTORY_IGNORE')"
                                        width="35" height="35"></lx-btn>
                              </div>

                              <div @keydown.enter="changeFormStatus(form, 'delete', false, index)">
                                <lx-btn small classes="mr-1 text-none"
                                        as-icon
                                        as-text
                                        icon-size="18"
                                        @click="changeFormStatus(form, 'delete', false, index)"
                                        icon="delete_forever"
                                        :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_DELETE')"
                                        width="35" height="35"></lx-btn>
                              </div>
                            </div>
                          </div>
                        </ValidationObserver>
                      </div>
                    </lx-expansion-panel-header-with-slot>

                    <lx-expansion-panel-content :style="{'background': compColor.lxExpansion.background}">
                      <ValidationObserver tag="div" :ref="'forms.' + index + '.content'" class="layout-column flex">
                        <component v-bind:is="formComponentsMapping[details.forms[index].formType]"
                                   :path="'forms.' + index"
                                   :recentForms="details.recentForms"
                                   :additionalItemsList="additionalItemsList"
                                   :date="details.forms[index].date"
                                   :dateOfDeath="details.dateOfDeath"
                                   :diagDate="getDiagnosisDateForForms(details.forms[index].diagnosis)"
                                   :unlinkedRecentSystemicTherapies="details.unlinkedRecentSystemicTherapies"
                                   :validationRef="$refs['forms.' + index + '.content'] && $refs['forms.' + index + '.content'][0] ? $refs['forms.' + index + '.content'][0] : {}"
                                   v-model="details.forms[index]"
                                   class="tab"></component>
                      </ValidationObserver>
                    </lx-expansion-panel-content>
                  </lx-expansion-panel>

                  <!--  Manuelle Eingabe zusätzlicher Meldeanlässe  -->

                  <div class="flex-100 px-2 py-1 my-2 mb-0 mx-2 sub-headline" style="border-radius: 4px;">
                    <span class="body-2 font-weight-bold" :style="{color: compColor.primeColor}">{{ $t('COMPONENT_IMPORT.ADDITIONAL_REPORTS') }}</span>
                  </div>

                  <lx-expansion-panel class="mx-2" :tooltip-text-open-icon="$t('COMPONENT_IMPORT.TOOLTIP_EXPAND_PANEL')">
                    <lx-expansion-panel-header-with-slot style="border-radius: 6px;">
                      <div class="flex layout-row">
                        <ValidationObserver tag="div" :ref="'newAdditionalForm.header'" class="layout-row flex">
                          <div class="flex layout-row px-3 py-3">

                            <!-- Datum -->

                            <div class="pr-2 flex-gt-sm-15">
                              <validation-provider tag="div"
                                                   name="newAdditionalForm.date"
                                                   ref="newAdditionalForm.date"
                                                   :rules="returnRulesForDateValues(newAdditionalForm)"
                                                   v-slot="{ errors }">
                                <lx-datepicker
                                  :error="errors[0]"
                                  :label="$t('COMPONENT_IMPORT.DATE') + ' *'"
                                  class="mr-1 mb-1"
                                  @change="setTNMDateInDependencyToDate"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  v-model="newAdditionalForm.date"
                                  dense
                                ></lx-datepicker>
                              </validation-provider>
                            </div>

                            <!-- Meldeanlass -->

                            <div class="px-2 flex-gt-sm-20">
                              <validation-provider tag="div"
                                                   :name="'newAdditionalForm.reportType'"
                                                   rules="required"
                                                   v-slot="{ errors }">
                                <v-select
                                  :color="compColor.primeColor"
                                  :label="$t('COMPONENT_IMPORT.REPORT_TYPE') + ' *'"
                                  v-model="newAdditionalForm.reportDisplayName"
                                  :items="reportTypesNewForms"
                                  @change="setFormValues(newAdditionalForm, true)"
                                  outlined
                                  class="mb-1"
                                  :error-messages="errors[0]"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  :dark="compColor.isInDarkMode"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  dense>
                                  <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on"
                                                 :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                      <v-list-item-content class="py-0">
                                        <label style="cursor:pointer;"
                                               :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                               class="lighten-4 text--darken-2">{{ item.text }}</label>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </template>
                                </v-select>
                              </validation-provider>
                            </div>

                            <!-- ICD-10-Code Primärtumor -->

                            <div class="px-2 flex-gt-sm-20">
                              <validation-provider tag="div"
                                                   class="layout-row"
                                                   :name="'newAdditionalForm.diagnosis'"
                                                   rules="required"
                                                   v-slot="{ errors }">
                                <v-select
                                  :color="compColor.primeColor"
                                  :label="$t('COMPONENT_IMPORT.DIAGNOSIS') + ' *'"
                                  v-model="newAdditionalForm.diagnosis"
                                  :items="details.diagnoses"
                                  item-value="uuid"
                                  @change="validateDateAfterDiagnoseSelection('newAdditionalForm.date', newAdditionalForm)"
                                  outlined
                                  :disabled="details.diagnoses && details.diagnoses.length===1 && newAdditionalForm.diagnosis && newAdditionalForm.reportDisplayName==='tod'"
                                  class="mb-1"
                                  :hideDetails="!errors || errors && errors.length <= 0"
                                  :error-messages="errors[0]"
                                  :dark="compColor.isInDarkMode"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  dense>
                                  <template v-slot:selection='{ item }'>
                                    <span v-if="!compColor.isInDarkMode" class="label_no_wrap"
                                          :style="{ 'color': details.diagnoses.length === 1 && newAdditionalForm.reportDisplayName==='tod'? 'rgba(0, 0, 0, 0.38)' : '#445a64', 'font-size': '14.4px !important'}">
                                      {{ formatDiagnosisText(item) }}</span>
                                    <span v-if="compColor.isInDarkMode" class="label_no_wrap"
                                          :style="{ 'color': details.diagnoses.length === 1 && newAdditionalForm.reportDisplayName==='tod' ? 'rgba(255, 255, 255, 0.5)' : 'white', 'font-size': '14.4px !important'}">
                                      {{ formatDiagnosisText(item) }}</span>
                                  </template>
                                  <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on"
                                                 :style="{ 'cursor': attrs.disabled ? 'default !important' : 'pointer' }"
                                                 :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                      <v-list-item-content class="py-0">
                                        <label :style="{ 'cursor': attrs.disabled ? 'default !important' : 'pointer' }"
                                               :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? [attrs.disabled ? 'grey--text' : 'white--text'] : [attrs.disabled ? 'grey--text text--lighten-2' : '']"
                                               class="lighten-4">{{ formatDiagnosisText(item) }}</label>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </template>
                                </v-select>

                                <v-tooltip bottom open-delay="1000" max-width="800" v-if="newAdditionalForm.reportDisplayName==='tod'">
                                  <template v-slot:activator="{ on }">
                                    <div v-on="on" class="layout-row layout-align-start-start ml-1" :class="{'mt-2': denseFields, 'mt-3': !denseFields}"
                                         style="cursor: pointer;">
                                      <v-icon :color="compColor.prime" medium>info</v-icon>
                                    </div>
                                  </template>

                                  <p style="text-align: justify;" class="my-1">{{ $t('FORMS_DIVERSE.MODULE_DEATH_DIAGNOSIS_HINT') }}</p>
                                </v-tooltip>
                              </validation-provider>
                            </div>

                            <!-- Melder bei BAG -->

                            <div class="px-2 flex-gt-sm-15" v-if="isMedicalCommunity">
                              <validation-provider tag="div"
                                                   :name="'newAdditionalForm.reporter'"
                                                   rules="required"
                                                   v-slot="{ errors }">
                                <v-select
                                  :color="compColor.primeColor"
                                  :dark="compColor.isInDarkMode"
                                  :items="physicianList"
                                  outlined
                                  dense
                                  :error-messages="errors[0]"
                                  class="mb-0"
                                  hide-details
                                  :value-comparator="compareReporterInList"
                                  :label="$t('COMPONENT_CANCER_MESSAGE_DETAILS.CARE_PROVIDER') + ' *'"
                                  v-model="newAdditionalForm.reporter"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                >
                                  <template v-slot:selection='{ item }'>
                                    <span v-if="!compColor.isInDarkMode" class="label_no_wrap" :style="{ 'color': '#445a64'}">
                                      {{ item.value.name }}</span>
                                    <span v-if="compColor.isInDarkMode" class="label_no_wrap" :style="{ 'color': 'white'}">
                                      {{ item.value.name }}</span>
                                  </template>
                                  <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on"
                                                 :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                      <v-list-item-content class="py-0 layout-row">
                                        <div  class="py-0 layout-row">
                                          <lx-text-highlight :text="item.value.name" :search-string="item.value.name"
                                                             :color="compColor.prime"></lx-text-highlight>
                                          <span v-if="item.value.lanr" class="ml-2" :style="{ color: compColor.isInDarkMode ? '#546E7A' : 'rgba(0, 0, 0, 0.7)'}">(LANR: {{ item.value.lanr }} | Melder-ID: {{ item.value.melderId }})</span>
                                          <span v-if="item.value.iknr" class="ml-2" :style="{ color: compColor.isInDarkMode ? '#546E7A' : 'rgba(0, 0, 0, 0.7)'}">(IKNR: {{ item.value.iknr }} | Melder-ID: {{ item.value.melderId }})</span>
                                        </div>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </template>
                                </v-select>
                              </validation-provider>
                            </div>

                            <div class="flex"></div>

                            <v-divider vertical class="mx-2"></v-divider>

                            <!-- Buttons -->

                            <div class="layout-row layout-align-center-center">
                              <div @keydown.enter="changeFormStatus(newAdditionalForm, 'complete', true)">
                                <lx-btn small classes="mr-1 text-none"
                                        :disabled="formIsInValid('newAdditionalForm')"
                                        as-icon
                                        as-text
                                        icon-color="green"
                                        icon-size="20"
                                        @click="changeFormStatus(newAdditionalForm, 'complete', true)"
                                        icon="mdi-briefcase-check"
                                        :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_REPORT_REASON_TO_HISTORY_COMPLETE')"
                                        width="35" height="35"></lx-btn>
                              </div>

                              <div @keydown.enter="changeFormStatus(newAdditionalForm, 'import', true)">
                                <lx-btn small classes="mr-1 text-none"
                                        :disabled="formHeaderIsInValid('newAdditionalForm')"
                                        as-icon
                                        as-text
                                        :icon-color="btnColorSelected"
                                        icon-size="18"
                                        @click="changeFormStatus(newAdditionalForm, 'import', true)"
                                        icon="mdi-check-circle"
                                        :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_REPORT_REASON_TO_HISTORY')"
                                        width="35" height="35"></lx-btn>
                              </div>

                              <div @keydown.enter="changeFormStatus(newAdditionalForm, 'ignore', true)">
                                <lx-btn small classes="mr-1 text-none"
                                        :disabled="formHeaderIsInValid('newAdditionalForm')"
                                        as-icon
                                        as-text
                                        icon-size="18"
                                        :icon-color="btnColorIgnored"
                                        @click="changeFormStatus(newAdditionalForm, 'ignore', true)"
                                        icon="no_encryption"
                                        :tooltip="$t('COMPONENT_IMPORT.BTN_TOOLTIP_REPORT_REASON_TO_HISTORY_IGNORE')"
                                        width="35" height="35"></lx-btn>
                              </div>
                            </div>
                          </div>
                        </ValidationObserver>
                      </div>
                    </lx-expansion-panel-header-with-slot>

                    <lx-expansion-panel-content :style="{'background': compColor.lxExpansion.background}">
                      <ValidationObserver tag="div" :ref="'newAdditionalForm.content'" class="layout-column flex">
                        <component v-bind:is="formComponentsMapping[newAdditionalForm.formType]"
                                   path="newAdditionalForm"
                                   v-model="newAdditionalForm"
                                   :recentForms="details.recentForms"
                                   :date="newAdditionalForm.date"
                                   :additionalItemsList="additionalItemsList"
                                   :unlinkedRecentSystemicTherapies="details.unlinkedRecentSystemicTherapies"
                                   :unlinkedRecentRadiationTherapies="details.unlinkedRecentRadiationTherapies"
                                   :isAdditionalImportForm="true"
                                   :dateOfDeath="details.dateOfDeath"
                                   :diagDate="getDiagnosisDateForForms(newAdditionalForm.diagnosis)"
                                   :validationRef="$refs['newAdditionalForm.content'] ? $refs['newAdditionalForm.content'] : {}"
                                   :icd="getDiagnosisForForms(newAdditionalForm.diagnosis)"
                                   :reportType="newAdditionalForm.reportDisplayName"
                                   class="tab"></component>
                      </ValidationObserver>
                    </lx-expansion-panel-content>
                  </lx-expansion-panel>
                </lx-expansion-panels>
              </lx-expansion-panel-content>
            </lx-expansion-panel>
          </lx-expansion-panels>
        </div>

        <div v-show="showBaseData" style="overflow-y: scroll;">
          <ValidationObserver tag="div" :ref="'baseData'">
            <base-data class="flex pa-2" style="overflow-y: scroll;"
                       :validationRef="$refs.baseData"
                       v-model="details.patient"></base-data>
          </ValidationObserver>
        </div>
      </ValidationObserver>
    </div>

    <!-- ignore dialog -->

    <popup-blacklist-duration
        :show-duration-popup="showDurationDialog"
        @set-show-blacklist-duration-dialog="ignorePatientForImportWithDuration">
    </popup-blacklist-duration>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'

import baseData from './PatientBaseData'
import registerDetails from './subForms/Register'
import progressDetails from './subForms/Progress'
import operationDetails from './subForms/Operation'
import radiationTherapy from './subForms/RadiationTherapy'
import systemicTherapy from './subForms/SystemicTherapy'
import tumorConference from './subForms/TumorConference'
import death from './subForms/Death'
import LxAutocomplete from '../../components/lx_auto_complete/LxAutocomplete.vue'
import popupBlacklistDuration from '../../components/patients/PopupBlacklistDuration.vue'

import metaData from '../../components/documentation/shared/formsMetaData'
import utils from '../../common/utils'

export default {
  name: 'ImportDetails',
  mixins: [metaData, utils],
  components: {
    LxAutocomplete,
    baseData,
    registerDetails,
    progressDetails,
    operationDetails,
    radiationTherapy,
    systemicTherapy,
    tumorConference,
    death,
    popupBlacklistDuration
  },
  props: {
    detailId: {
      type: String
    },
    reloadTrigger: {
      type: Boolean
    },
    exportPeriodFrom: {
      type: [Object, String]
    }
  },
  data () {
    return {
      sideLocalizationsPairedTumor: [
        {value: 'R', text: 'Rechts'},
        {value: 'L', text: 'Links'},
        {value: 'B', text: 'Beidseitig (bei bestimmten Tumoren 2 Meldungen angeben)'},
        {value: 'U', text: 'Unbekannt'}
      ],
      sideLocalizations: [
        {value: 'R', text: 'Rechts'},
        {value: 'L', text: 'Links'},
        {value: 'B', text: 'Beidseitig'},
        {value: 'M', text: 'Mittellinie/mittig'},
        {value: 'U', text: 'Unbekannt'},
        {value: 'T', text: 'Trifft nicht zu'}
      ],
      diagnosticAssurances: [
        {value: '1', text: 'Klinisch ohne tumorspezifische Diagnostik (nur körperliche Untersuchung)'},
        {value: '2', text: 'Klinisch: Klinische Diagnose vor dem Sterbedatum durchgeführt'},
        {value: '4', text: 'Spezifische Tumormarker'},
        {value: '5', text: 'Zytologisch'},
        {value: '6', text: 'Histologie einer Metastase'},
        {value: '7', text: 'Histologisch: Histologie des Primärtumors'},
        {value: '9', text: 'Unbekannt'}
      ],
      reportTypes: [],
      formTypes: [
        {value: 'register', text: 'Diagnose/Aufnahme'},
        {value: 'progress', text: 'Verlauf'},
        {value: 'operation', text: 'Operation'},
        {value: 'radiationTherapy', text: 'Strahlentherapie'},
        {value: 'systemicTherapy', text: 'Systemische Therapie'},
        {value: 'tumorConference', text: 'Tumorkonferenz'}
      ],
      formComponentsMapping: {
        register: 'registerDetails',
        progress: 'progressDetails',
        operation: 'operationDetails',
        radiationTherapy: 'radiationTherapy',
        systemicTherapy: 'systemicTherapy',
        tumorConference: 'tumorConference',
        death: 'death'
      },
      details: {
        patient: {
          baseData: {
            insurance_data: {},
            address: {},
            former_names: []
          },
          patientDiff: {}
        },
        diagnoses: [],
        unlinkedRecentSystemicTherapies: []
      },
      oldModelForms: [],
      panel: [0, 1, 2],
      formsPanel: [],
      reportTypesImportedForms: null,
      mappingReportTypes: null,
      FM_LokalisationTypes: null,
      isLoading: false,
      showBaseData: false,
      newAdditionalForm: {
        isAdditionalForm: true,
        date: null
      },
      reportTypesNewForms: [],
      registerWithAfterCare: [],
      additionalItemsList: [],
      Tumorkonferenz_TypTypes: null,
      SYST_Ende_GrundTypes: null,
      DateAccuracyLongTypes: null,
      btnColorIgnored: 'grey',
      btnColorSelected: '#e8b20e',
      btnColorCompleted: 'green',
      showIgnoreDialog: false,
      oldPatientModel: {},
      Primaertumor_ICD_VersionTypes: null,
      Primaertumor_ICD_O_VersionTypes: null,
      resultSearchModel: {},
      PairedTumorList: [],
      PairedAdditionalTumors: {},
      NotPairedAdditionalTumors: {},

      showDurationDialog: false
    }
  },
  created () {
    this.registerWithAfterCare = this.formsMetaData.registerWithAfterCare
    this.reportTypesImportedForms = this.formsMetaData.reportTypesImportedForms
    this.reportTypes = this.formsMetaData.reportTypes
    this.mappingReportTypes = this.formsMetaData.mappingReportTypes
    this.FM_LokalisationTypes = this.formsMetaData.FM_LokalisationTypes
    this.Tumorkonferenz_TypTypes = this.formsMetaData.Tumorkonferenz_TypTypes
    this.SYST_Ende_GrundTypes = this.formsMetaData.SYST_Ende_GrundTypes
    this.DateAccuracyLongTypes = this.formsMetaData.DateAccuracyLong
    this.Primaertumor_ICD_VersionTypes = this.filterVersions('icd10')
    this.Primaertumor_ICD_O_VersionTypes = this.filterVersions('icdO')
    this.PairedTumorList = this.formsMetaData.PairedTumorList
    this.PairedAdditionalTumors = this.formsMetaData.PairedAdditionalTumors
    this.NotPairedAdditionalTumors = this.formsMetaData.NotPairedAdditionalTumors

    this.getData()
  },
  mounted () {
    if (typeof this.createCategory === "function" && typeof this.addEntryToMenu === "function") {
      this.createCategory('BlackListDialog')
    }
  },
  methods: {
    /**
     * Open special context menu for selected patient
     *
     * @param event The opening event
     */
    openContextOnRowClick (event) {
      const self = this

      this.addEntryToMenu({
        type: 'ImportDetails',
        value: [
          {
            icon: true,
            iconName: 'mdi-delete-outline',
            title: 'Patient/in für diesen Import verwerfen',
            action: this.ignorePatientForImportOnce
          },
          {
            icon: true,
            iconName: 'mdi-ghost',
            title: this.$t('COMPONENT_IMPORT.OVERVIEW_CONTEXT_MENU_IGNORE_EVER'),
            action: function () {
              self.showDurationDialog = true
            }
          }
        ]
      })

      this.$lxContext.open(event, 'ImportDetails', {_id: this.details?._id})
    },

    /**
     * Reset changes in base data of patient
     */
    clearPatientForm () {
      this.$set(this.details, 'patient', lodash.cloneDeep(this.oldPatientModel))

      if (this.$refs.baseDataHeader) {
        this.$refs.baseDataHeader.reset()
      }

      if (this.$refs.baseData) {
        this.$refs.baseData.reset()
      }
    },

    /**
     * Return validation rules for date values
     *
     * @param form
     * @returns {{date_must_be_greater_than: (*|undefined), german_date: boolean, date_must_be_lesser_than_death: (*|string)[], required: boolean, date_not_in_future: boolean, date_must_be_greater_than_diagnosis: *}}
     */
    returnRulesForDateValues (form) {
      let dateAccuracy = null

      // add date accuracy as rule param for systemic therapy
      if (form.formType === 'systemicTherapy' && form.reportType === 'behandlungsbeginn' && form?.additionalFields?.systemicTherapy?.syst_beginn_datum_genauigkeit) {
        dateAccuracy = form.additionalFields.systemicTherapy.syst_beginn_datum_genauigkeit
      }

      // add date accuracy as rule param for systemic therapy
      if (form.formType === 'tumorConference' && form?.additionalFields?.tumorConference?.datum_genauigkeit) {
        dateAccuracy = form.additionalFields.tumorConference.datum_genauigkeit
      }

      let rulesObject = {
        required: true,
        german_date: true,
        date_not_in_future: true,
        date_must_be_greater_than_diagnosis: this.getDiagnosisDateForForms(form.diagnosis),
        date_must_be_lesser_than_death: [this.details.dateOfDeath, dateAccuracy]
      }

      // add rule, that end date must be later than start date in systemic therapy
      if (form.formType === 'systemicTherapy' && form.reportType === 'behandlungsende') {
        rulesObject.date_must_be_greater_than = form.additionalFields.systemicTherapy.syst_beginn_datum
      }

      // add rule, that end date must be later than start date in radiation therapy
      if (form.formType === 'radiationTherapy' && form.reportType === 'behandlungsende') {
        rulesObject.date_must_be_greater_than = form.additionalFields.radiationTherapy.bestrahlung[0].st_beginn_datum
      }

      // add rule, that death date must be the latest date, if patient already in documentation
      if (form.formType === 'death' && this.details.originPatientId) {
        rulesObject.death_date_must_be_latest = [this.details.originPatientId, false]
      }

      // add rule, that death date must be the latest date, if new patient (originPatientId does not exist)
      if (form.formType === 'death' && !this.details.originPatientId) {
        rulesObject.death_date_must_be_latest_new_Import = [this.details.recentForms]
      }

      return rulesObject
    },

    /**
     *  Compare selected reporter with reporter in list to show in field if lanr are identical
     *
     * @param arrayElement The element from list of physicians
     * @param selectedItem The selected reporter
     * @returns {boolean}
     */
    compareReporterInList (arrayElement, selectedItem) {
      if (arrayElement?.lanr && selectedItem?.lanr && arrayElement?.lanr === selectedItem?.lanr) return true
      else if (arrayElement?.iknr && selectedItem?.iknr && arrayElement?.iknr === selectedItem?.iknr) return true
      return false
    },

    /**
     * Disable/Enable import-button in dependency to changes in report reasons or base data
     *
     * @returns {boolean|*}
     */
    disableImportButton () {
      if (this.details.originPatientId) {
        // existing patient
        // must have a form to import, manually changed baseData with no patient diff or saved changes in base data without patient diff
        const baseDataHasChanges = this.baseDataHasChanges || this.details.patient.isPatientModified

        return !(!this.baseDataAreInvalid() && !this.patientDiffExists(true) && !this.areImportFormsInvalid() && (this.atLeastOneFormIsReadyToImport || baseDataHasChanges))
      } else {
        // new patient
        // must have valid import forms, must have at least one diagnosis and base data must be valid
        return this.areImportFormsInvalid() || !this.atLeastOneDiagnosisIsSelected  || this.baseDataAreInvalid()
      }
    },

    /**
     * Set date of tnm in progress form of new additional form in dependency to leading date
     */
    setTNMDateInDependencyToDate () {
      this.$nextTick(() => {
        // const displayNamesForTNM = ['statusaenderung_metastase', 'vollremission_cont', 'vollremission_new']
        const displayNamesForTNM = ['statusaenderung_metastase']

        if (this.newAdditionalForm && displayNamesForTNM.indexOf(this.newAdditionalForm.reportDisplayName) > -1 && this.newAdditionalForm.formType === 'progress' && this.newAdditionalForm.date) {
          this.newAdditionalForm.additionalFields = this.newAdditionalForm.additionalFields || {}
          this.newAdditionalForm.additionalFields.progress = this.newAdditionalForm.additionalFields.progress || {}
          this.newAdditionalForm.additionalFields.progress.tnm = this.newAdditionalForm.additionalFields.progress.tnm || {}

          this.$set(this.newAdditionalForm.additionalFields.progress.tnm, 'tnm_datum', this.newAdditionalForm.date)
        }
      })
    },

    /**
     * Switch between datepicker and list of available dates in list of systemic therapy
     *
     * @param form The selected systemic therapy
     * @param value Boolean value
     */
    switchDateStateOfSystemicTherapy (form, value) {
      this.$set(form, 'dateIsSelected', value)
    },

    /**
     * Set uuid to systemic therapy after selection of date from dates list
     *
     * @param form The selected systemic therapy
     */
    setUUIDOfSystemicTherapy (form) {
      const listItem = (form.datesList || []).find(item => item && item.date && item.date === form.date)

      if (listItem && listItem.uuid) {
        form.uuid = listItem.uuid
      }
    },

    /**
     * Set new additionalForm-values after undo
     */
    setDefaultValuesOfAdditionalForm () {
      this.$set(this, 'newAdditionalForm', {isAdditionalForm: true})

      if (this.dateSelection && Array.isArray(this.dateSelection) && this.dateSelection.length > 0) {
        this.newAdditionalForm.date = this.dateSelection[this.dateSelection.length - 1]
      }

      // set reporter, if is it a medical community with only one member
      if (this.isMedicalCommunity && this.physicianList && Array.isArray(this.physicianList) && this.physicianList.length === 1) {
        this.newAdditionalForm.reporter = this.physicianList[0].value
      }
    },

    /**
     *  Enable/Disable undo button of possible report reasons in dependency to old model value
     */
    formIsPristine (index) {
      if (this.details?.forms && this.details.forms[index] && this.oldModelForms[index]) {
        return lodash.isEqual(this.details.forms[index], this.oldModelForms[index])
      }

      return false
    },

    /**
     * Enable/Disable undo button of diagnosis in dependency to pristine-flag
     */
    diagnosisIsPristine (ref) {
      if (Array.isArray(this.$refs[ref + '.header']) && this.$refs[ref + '.header'][0] && this.$refs[ref + '.header'][0].flags) {
        return this.$refs[ref + '.header'][0].flags.pristine
      } else if (this.$refs[ref + '.header'] && this.$refs[ref + '.header'].flags) {
        return this.$refs[ref + '.header'].flags.pristine
      }

      return true
    },

    /**
     * Return label for button "Stammdaten" in dependency to new patient or not
     */
    formatLabelOfBaseData () {
      let label = 'Stammdaten'

      if (!this.details.originPatientId) label += ' (Neuer Patient)'
      return label
    },

    /**
     * Return modified string for report reason in history
     *
     * @param form The form in history
     * @returns {string|string}
     */
    setReportReasonInHistory (form) {
      let progressState
      let returnValue

      switch (form.formType) {
        case 'register':
          return 'Erstdiagnose'

        case 'diagnosis':
          return 'Erstdiagnose'

        case 'progress':
          // show report reason in dependency of reportDisplayName, if exists set more detailed reason
          if (form.reportDisplayName && this.reportTypes.find(item => item.value === form.reportDisplayName)) {
            returnValue = (this.reportTypes.find(item => item.value === form.reportDisplayName) || {}).text
          } else {
            progressState = form?.additionalFields?.progress?.gesamtbeurteilung_tumorstatus ? form.additionalFields.progress.gesamtbeurteilung_tumorstatus : null
            returnValue = progressState && progressState === 'K' ? 'Statusmeldung' : 'Statusänderung'
          }

          return returnValue

        case 'systemicTherapy':
          return form.reportType === 'behandlungsbeginn' ? 'Beginn syst. Therapie' : 'Ende syst. Therapie'

        case 'radiationTherapy':
          return form.reportType === 'behandlungsbeginn' ? 'Beginn Strahlentherapie' : 'Ende Strahlentherapie'

        case 'operation':
          return 'Operation'

        case 'tumorConference':
          return 'Tumorkonferenz'

        case 'death':
          return 'Tod'

        default:
          return 'Statusmeldung'
      }
    },

    /**
     * Filter diagnoses to show in diagnoses area
     */
    filterDiagnoses (arr) {
      return (arr || []).filter(item => {
        if (!item['isRecent'] && !item.importStatus) {
          return item
        }
      })
    },

    /**
     * Switch between baseData and diagnoses view. On change to diagnoses save base data
     *
     * @param newView Name of new View baseData or diagnoses
     **/
    switchViews (newView) {
      switch (newView) {
        case 'baseData':
          this.showBaseData = true

          // is necessary because to avoid blocking getData
          this.isLoading = false
          this.getData()

          if (this.$refs.baseData) {
            this.$refs.baseData.validate()
          }
          if (this.$refs.baseDataHeader) {
            this.$refs.baseDataHeader.validate()
          }
          break
        case 'diagnoses':
          if (this.isLoading) return
          this.isLoading = true

          this.$http.put('/import/patient/details', {_id: this.details._id, patient: this.details.patient.baseData, patientDiff: this.details.patient.patientDiff})
            .then(() => {
              this.showBaseData = false

              // is necessary because to avoid blocking getData
              this.isLoading = false
              this.getData()
            })
            .catch((error) => {
              this.populateFormError(this.$refs.baseData, error)
                .catch(this.NotifyError)
            })
            .finally(() => {
              this.isLoading = false
            })
          break
      }
    },

    patientDiffExists (patientDiffPositive) {
      if (patientDiffPositive) {
        return this.details.patient.patientDiff && Object.keys(this.details.patient.patientDiff).length > 0
      } else {
        return !this.details.patient || !this.details.patient.patientDiff || Object.keys(this.details.patient.patientDiff).length === 0
      }
    },

    /**
     * Undo form to default form from import
     *
     * @param form The form, which should be reset
     * @param path the path of selected form (diagnoses or forms)
     * @param index The index of form in array
     */
    undoForm (form, path, index) {
      if (this.isLoading) return
      this.isLoading = true

      // get origin form by uuid
      this.$http.get('/import/patient/details/' + this.detailId + '/originForm/' + form.uuid)
        .then(response => {
          if (response.data) {
            this.$set(this.details[path], index, response.data)
            this.isLoading = false

            if (this.$refs[path + '.' + index + '.header']) {
              this.$refs[path + '.' + index + '.header'][0].reset()
            }

            if (this.$refs[path + '.' + index + '.content']) {
              this.$refs[path + '.' + index + '.content'][0].reset()
            }

            // delete empty string of version to prevent validation errors, if version could not set because of invalid icd10-code
            if (this.details[path][index].formType === 'diagnosis' && this.details[path][index].icd10.version === '') {
              this.details[path][index].icd10.version = null
            }

            this.changeFormStatus(this.details[path][index], 'undo')
          }
        })
        .catch(error => {
          this.NotifyError(error)
          this.isLoading = false
        })
    },

    /**
     * Change state of form
     *
     * @param form
     * @param newState
     * @param isNewAdditionalForm
     * @param index The index of possible report reasons
     */
    changeFormStatus (form, newState, isNewAdditionalForm, index) {
      if (this.isLoading) return
      this.isLoading = true

      // delete additional frontend-field on systemic therapy to trigger view of date
      this.$delete(form, 'dateIsSelected')
      this.$delete(form, 'datesList')

      // set form to additional form when newState == 'import' or newState == 'complete'
      if (newState === "import" || newState === "complete") {
        form.isAdditionalForm = isNewAdditionalForm
      }

      // delete linked therapy for cases like undo end of systemic therapy in new form and then undo start form from possible report reason
      if (newState === 'undo' && form.formType !== 'diagnosis') {
        delete form.linked_therapy

        // delete linked diagnoses if selected form is not diagnosis and is not death with one existing diagnosis
        if (!(this.details.diagnoses.length === 1 && form.isAdditionalForm && form.reportType === 'tod')) {
          delete form.diagnosis
        }
      }

      // if state is undo and is additional form then set
      if (form.isAdditionalForm && newState === 'undo') {
        this.newAdditionalForm = lodash.cloneDeep(form)
        this.$delete(this.newAdditionalForm, 'importStatus')
        this.$delete(this.newAdditionalForm, 'uuid')
      }

      // update form data
      this.$http.put('/import/patient/form', {_id: this.detailId, newState: newState, form: form})
        .then(() => {
          if (isNewAdditionalForm) {
            // reset new additional form
            this.newAdditionalForm = lodash.cloneDeep(form)
            this.$delete(this.newAdditionalForm, 'formType')
            this.$delete(this.newAdditionalForm, 'linked_therapy')

            // delete header information of death, death can only exist one time
            if (this.newAdditionalForm.reportType === 'tod') {
              this.$delete(this.newAdditionalForm, 'reportType')
              this.$delete(this.newAdditionalForm, 'reportDisplayName')
            }

            // show message that additional report reason is successfully added to history
            const notifyOptions = {
              text: this.$t('COMPONENT_IMPORT.REPORT_REASON_SUCCESSFULLY_ADDED_TO_HISTORY'),
              duration: 6000,
              type: 'success'
            }

            this.NotifySystem(notifyOptions)

            this.setFormValues(this.newAdditionalForm, true, true)

            // close content of expansion panel of new additionalForm if open
            // panel index is always forms count + 1
            // are there no possible forms to import always one panel exists
            if (this.formsPanel.indexOf(this.details.forms.length + 1) > -1) {
              this.formsPanel.splice(this.formsPanel.indexOf(this.details.forms.length + 1))
            }

            this.$refs['newAdditionalForm.header'].reset()
            this.$refs['newAdditionalForm.content'].reset()
          }

          // delete index of selected form from panel to prevent opened content of following element after changing state
          if (index || index === 0) this.formsPanel.splice(index)
        })
        .catch(this.NotifyError)
        .finally(() => {
          // set flag on false to prevent problem with getData
          this.isLoading = false

          // reload details and prevent reset of new additional form after undo
          this.getData(!!form.isAdditionalForm)
        })
    },

    /**
     * Set additional items on form in dependency to existing additional items of selected cancer register
     *
     * @param form
     */
    setAdditionalItems (form) {
      // set additional items to form, if not exists
      this.additionalItemsList.forEach(listItem => {
        if (form.headerData.zusatzitems.findIndex((item) => item.name === listItem.name) === -1 && listItem.form_types.indexOf(form.formType) > -1) {
          const newIndex = form.headerData.zusatzitems.length

          this.$set(form.headerData.zusatzitems, newIndex, {})
          this.$set(form.headerData.zusatzitems[newIndex], 'name', listItem.name)
          this.$set(form.headerData.zusatzitems[newIndex], 'date', {})
          this.$set(form.headerData.zusatzitems[newIndex], 'value', null)
          this.$set(form.headerData.zusatzitems[newIndex], 'comment', null)
        }
      })
    },

    /**
     * Get all data of selected patient with all forms in import
     *
     * @param afterUndoAdditionalForm Is true after state change to undo of additional form
     */
    getData (afterUndoAdditionalForm) {
      if (this.isLoading) return
      this.isLoading = true

      this.$http.get('/import/patient/details/' + this.detailId)
        .then(response => {
          // return to overview when response object is empty
          if (!response.data || Object.keys(response.data).length === 0) {
            this.NotifyError('Beim Laden der Daten ist ein Fehler aufgetreten')
            this.returnToOverview(true)
          }

          response.data.patient.baseData.address = response.data.patient.baseData.address || {}
          response.data.patient.baseData.former_names = response.data.patient.baseData.former_names || []

          this.details = response.data
          this.oldPatientModel = lodash.cloneDeep(this.details.patient)
          this.additionalItemsList = this.details?.cancerRegister?.additional_items || []

          // add additional items to possible report reasons in dependency to cancer register
          this.details.forms.forEach(form => {
            if (!form.isAdditionalForm) {
              form.headerData.zusatzitems = form.headerData.zusatzitems || []

              // set additional items to form if exists in cancer register
              this.setAdditionalItems(form)
            }
          })

          this.oldModelForms = lodash.cloneDeep(this.details.forms)

          // reset new additional form only when no undo of additional form
          if (!afterUndoAdditionalForm) this.setDefaultValuesOfAdditionalForm()

          // copy ais-id from patient to clipboard
          const id = this.details.patient.baseData.ais_id
          // create textarea with text content to copy to clipboard
          let textArea = document.createElement('textarea')
          textArea.value = id
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          textArea.remove()

          // create list of report types in dependency to cancer register
          this.reportTypes = lodash.cloneDeep(this.formsMetaData.reportTypes)
          if (this.registerWithAfterCare.indexOf((this.details.cancerRegister || {}).name) > -1 && Array.isArray( this.reportTypes) && Array.isArray(this.formsMetaData.reportTypeAfterCare)) {
            this.reportTypes.splice(2, 0, ...this.formsMetaData.reportTypeAfterCare)
          }

          this.reportTypesNewForms = lodash.cloneDeep(this.reportTypes)

          // delete diagnosis elements from array for new forms
          this.reportTypesNewForms.shift()

          // search for death form in history or found forms to delete option for selection, if form already exists
          const deathInHistory = (this.details.recentForms || []).find(form => form.reportType === 'tod')
          const deathInForms = (this.details.forms || []).find(form => form.reportType === 'tod')

          if (deathInHistory || deathInForms) {
            this.reportTypesNewForms.pop()
          }

          // trigger validation after changing state of new additional form to set correct valid-state for buttons
          // getDate will be called after changing form-state
          // to prevent false behaviour of buttons of new additional form, elements  of new additional form must be validated
          if (afterUndoAdditionalForm) {
            this.$refs['newAdditionalForm.header'].validate()
            this.$refs['newAdditionalForm.content'].validate()
          }

        })
        .catch(error => {
          this.NotifyError(error)
          this.returnToOverview(true)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    /**
     * Return to overview of patients
     *
     * @param isAfterImportOrIgnore Bool to prevent update after import, ignore or error in getData
     */
    returnToOverview (isAfterImportOrIgnore) {
      // update patient not after import, ignore or error in getData
      if (this.showBaseData && !isAfterImportOrIgnore) {
        if (this.isLoading) return
        this.isLoading = true

        this.$http.put('/import/patient/details', { _id: this.details._id, patient: this.details.patient.baseData, patientDiff: this.details.patient.patientDiff })
          .then(() => {
            // Trigger 'setShowDetails()' on parent
            this.$emit('set-show-details', false)
          })
          .catch(this.NotifyError)
          .finally(() => {
            this.isLoading = false
          })
      } else {
        // Trigger 'setShowDetails()' on parent
        this.$emit('set-show-details', false)
      }
    },

    /**
     * Get ICD10-Code from associated diagnoses
     *
     * @param uuid The uuid from diagnosis to find in forms
     */
    getDiagnosisForForms (uuid) {
      return uuid ? this.details.diagnoses.find(elem => elem.uuid === uuid) : undefined
    },

    /**
     * Get date from associated diagnoses for validation of forms
     *
     * @param uuid The uuid from diagnosis to find in forms
     */
    getDiagnosisDateForForms (uuid) {
      return uuid ? this.details.diagnoses.find(elem => elem.uuid === uuid).date : undefined
    },

    /**
     * Validate date of form after selecting diagnosis
     */
    validateDateAfterDiagnoseSelection (ref, form) {
      const selectedDiagnosis = this.details.diagnoses.find(diagnosis => diagnosis.uuid === form.diagnosis)

      if (selectedDiagnosis && selectedDiagnosis.disabled) {
        // delete linked diagnosis, if selected diagnosis is disabled in selection
        // disabled diagnosis could be linked by clicking "c" in input-element and diagnosis is first element in selection
        this.$nextTick(() => {
          this.$set(form, 'diagnosis', undefined)
        })
      } else {
        // use timeout to be sure that validation rule of form-date has correct values
        setTimeout(() => {
          if (this.$refs[ref]) Array.isArray(this.$refs[ref]) ? this.$refs[ref][0].validate() : this.$refs[ref].validate()
        }, 200)
      }
    },

    /**
     * Disable or enable delete button of diagnosis in history in dependency to associated forms
     *
     * @param item
     * @returns {boolean}
     */
    isDiagnosisAssigned (item) {
      if (Array.isArray(this.details.forms) && Array.isArray(this.details.recentForms) && item.formType === 'diagnosis') {
        const recentFormIsSelected = this.details.recentForms.some(form => form.importStatus && form.importStatus !== 'delete' && form.diagnosis && form.diagnosis !== '' && form.diagnosis === item.uuid)
        // consider only forms in history (having importStatus)
        const formIsSelected = this.details.forms.some(form => form.importStatus && form.diagnosis && form.diagnosis !== '' && form.diagnosis === item.uuid)

        return recentFormIsSelected || formIsSelected
      }
      return false
    },

    /**
     * Set form values in dependency to selected report display type
     *
     * @param item
     * @param isNewForm
     * @param newFormAfterTakeOver True, if new form was taken over into the history
     */
    setFormValues (item, isNewForm, newFormAfterTakeOver) {
      // clone item to get possibility to compare changes of formType and set old additionalField-values in systemicTherapy
      const recentItem = lodash.cloneDeep(item)

      if (item && item.reportDisplayName && this.mappingReportTypes[item.reportDisplayName]) {
        this.$set(item, 'formType', this.mappingReportTypes[item.reportDisplayName].form)
        this.$set(item, 'reportType', this.mappingReportTypes[item.reportDisplayName].report)
        this.$set(item, 'headerData', {})
        this.$set(item.headerData, 'eigenleistung', 'J')
        this.$set(item.headerData, 'zusatzitems', [])

        // set additional items to form if exists in cancer register
        this.setAdditionalItems(item)

        // default values for new Form
        if (isNewForm) {
          let additionalFields = {}

          // Delete date, if new form is copy of other new form, which was taken over into the history
          if (newFormAfterTakeOver) this.$delete(item, 'date')

          // delete linked therapy, if exists to prevent problems between systemic therapy and radiation therapy
          this.$delete(item, 'linked_therapy')

          switch (item.formType) {
            case 'progress':
              additionalFields.progress = {
                prostata: {},
                tnm: {}
              }

              switch (item.reportDisplayName) {
                case 'statusaenderung':
                  // set tumor states if diagnosis with C61 exists
                  if (item.diagnosis) {
                    const icd10Code = this.details.diagnoses.find(elem => elem.uuid === item.diagnosis).icd10.code

                    if (icd10Code === 'C61') {
                      additionalFields.progress.verlauf_lokaler_tumorstatus = 'U'
                      additionalFields.progress.verlauf_tumorstatus_lymphknoten = 'U'
                      additionalFields.progress.verlauf_tumorstatus_fernmetastasen = 'U'
                    }
                  }
                  break
                case 'statusaenderung_metastase':
                  additionalFields.progress.gesamtbeurteilung_tumorstatus = 'P'
                  additionalFields.progress.verlauf_tumorstatus_fernmetastasen = 'R'
                  additionalFields.progress.fernmetastase = [{}]

                  // set tnm-values
                  additionalFields.progress.tnm.tnm_c_p_u_praefix_m = 'c'
                  additionalFields.progress.tnm.tnm_m = '1'
                  additionalFields.progress.tnm.tnm_version = '8'

                  if (item?.date) {
                    additionalFields.progress.tnm.tnm_datum = item.date
                    additionalFields.progress.fernmetastase[0].fm_diagnosedatum = item.date
                    additionalFields.progress.fernmetastase[0].fm_diagnosedatum_genauigkeit = 'E'
                  }
                  break
                case 'statusmeldungUN':
                  additionalFields.progress.gesamtbeurteilung_tumorstatus = 'K'
                  additionalFields.progress.verlauf_lokaler_tumorstatus = 'U'
                  additionalFields.progress.verlauf_tumorstatus_lymphknoten = 'U'
                  additionalFields.progress.verlauf_tumorstatus_fernmetastasen = 'U'
                  break
                case 'vollremission_cont': case 'vollremission_new':
                  additionalFields.progress.gesamtbeurteilung_tumorstatus = item.reportDisplayName === 'vollremission_new' ? 'V_new' : 'V_cont'
                  additionalFields.progress.verlauf_lokaler_tumorstatus = 'K'
                  additionalFields.progress.verlauf_tumorstatus_lymphknoten = 'K'
                  additionalFields.progress.verlauf_tumorstatus_fernmetastasen = 'K'

                  // // set tnm-values
                  // additionalFields.progress.tnm.tnm_c_p_u_praefix_t = 'c'
                  // additionalFields.progress.tnm.tnm_t = '0'
                  // additionalFields.progress.tnm.tnm_c_p_u_praefix_n = 'c'
                  // additionalFields.progress.tnm.tnm_n = '0'
                  // additionalFields.progress.tnm.tnm_c_p_u_praefix_m = 'c'
                  // additionalFields.progress.tnm.tnm_m = '0'
                  //
                  // if (item?.date) {
                  //   additionalFields.progress.tnm.tnm_datum = item.date
                  // }
                  break
                default:
                // additionalFields.progress.gesamtbeurteilung_tumorstatus = 'K'
                // additionalFields.progress.verlauf_lokaler_tumorstatus = 'U'
                // additionalFields.progress.verlauf_tumorstatus_lymphknoten = 'U'
                // additionalFields.progress.verlauf_tumorstatus_fernmetastasen = 'U'
              }
              break
            case 'radiationTherapy':
              // reset systemic form values only after changing form type (e.g. operation -> radiationTherapy)
              if (item.formType !== recentItem.formType) {
                additionalFields.radiationTherapy = {
                  bestrahlung: [
                    {
                      selectedKindOfApplication: 'perkutan',
                      selectedVersionOfDestination: 'code_version_2021',
                      st_applikationsart: {
                        perkutan: {
                          zielgebiet: {},
                          gesamtdosis: {},
                          einzeldosis: {}
                        }
                      }
                    }
                  ]
                }

                if (item.reportType === 'behandlungsbeginn') {
                  additionalFields.radiationTherapy.bestrahlung[0].st_beginn_datum = item.date

                  additionalFields.radiationTherapy.st_nebenwirkung = {
                    grad_maximal_2_oder_unbekannt: 'U'
                  }
                } else {
                  additionalFields.radiationTherapy.bestrahlung[0].st_ende_datum = item.date
                }

              } else {
                // fill additional fields with old values when formType-value has not changed
                additionalFields = recentItem.additionalFields
              }

              break
            case 'operation':
              additionalFields.operation = {
                op_intention: 'K',
                op_ops: [{}],
                residualstatus: {
                  lokale_beurteilung_residualstatus: 'RX'
                },
                operateure: [{}],
                op_komplikation: {
                  komplikation_nein_oder_unbekannt: 'N',
                  kuerzel: {}
                }
              }
              break
            case 'systemicTherapy':
              // reset systemic form values only after changing form type (e.g. operation -> systemicTherapy)
              if (item.formType !== recentItem.formType) {
                additionalFields.systemicTherapy = {}

                if (item.reportType === 'behandlungsbeginn') {
                  additionalFields.systemicTherapy.syst_nebenwirkung = {
                    grad_maximal_2_oder_unbekannt: 'U'
                  }
                }
              } else {
                // fill additional fields with old values when formType-value has not changed
                additionalFields = recentItem.additionalFields
              }

              // add date of accuracy to start date, if not exists
              if (!additionalFields.systemicTherapy.syst_beginn_datum_genauigkeit) {
                additionalFields.systemicTherapy.syst_beginn_datum_genauigkeit = 'E'
              }

              break
            case 'tumorConference':
              additionalFields.tumorConference = {
                datum_genauigkeit: 'E',
                therapieempfehlung: {
                  typ_therapieempfehlungen: ['KW']
                }
              }

              break
            case 'death':
              additionalFields.death = {}

              // set diagnosis only when it is recent or is ready to import
              if (this.details.diagnoses.length === 1 && this.details.diagnoses[0].isRecent || (this.details.diagnoses[0].importStatus && this.details.diagnoses[0].importStatus !== 'delete')) {
                item.diagnosis = this.details.diagnoses[0].uuid
              }
              break
            default:
              additionalFields.register = {}
          }

          this.$set(item, 'additionalFields', additionalFields)
        }

        if (item.formType !== 'systemicTherapy') {
          delete item.agents

          // delete possible elements of end of therapy, when report reason is beginn
          if (item.reportDisplayName !== 'behandlungsbeginnRT' && item.additionalFields.radiationTherapy) {
            delete item.additionalFields.radiationTherapy.st_intention

            if (item.additionalFields.radiationTherapy.bestrahlung) {
              delete item.additionalFields.radiationTherapy.bestrahlung.st_beginn_datum
            }
          }
        } else {
          // reset agents only for new additional form and after changing form type (e.g. operation -> systemicTherapy)
          if (isNewForm && item.formType !== recentItem.formType) {
            this.$set(item, 'agents', [{}])
          }

          delete item.additionalFields.systemicTherapy.syst_beginn_datum

          // delete possible elements of end of therapy, when report reason is beginn
          if (item.reportDisplayName !== 'behandlungsendeSyst') {
            delete item.additionalFields.systemicTherapy.syst_ende_grund
          }
        }
      }
    },

    /**
     * Import patient to documentation and delete him from import
     */
    importPatient () {
      if (this.isLoading) return
      this.isLoading = true

      // save possible changes
      this.$http.put('/import/patient/details', {_id: this.details._id, patient: this.details.patient.baseData, patientDiff: this.details.patient.patientDiff})
        .then(() => {
          // complete import
          this.$http.put('/import/patient/complete/' + this.detailId)
            .then(() => {
              // update tab-header of already existing patient if exists (after name changing, tab header will be updated)
              if (this.details?.originPatientId) {
                const tabs = this.getTabsFromStore
                const found = tabs.find((tab) => tab && tab.routeParams && tab.routeParams.patientId && tab.routeParams.patientId === this.details.originPatientId)

                if (found?.routeParams?.tabid) {
                  this.updateTabTitle({
                    id: found.routeParams.tabid,
                    headline: this.details.patient.baseData.lastname + ', ' + this.details.patient.baseData.firstname
                  })
                }
              }

              this.returnToOverview(true)
            })
            .catch(this.NotifyError)
            .finally(() => {
              this.isLoading = false
            })
        })
        .catch(error => {
          this.NotifyError(error)
          this.isLoading = false
        })
    },

    /**
     * Set flag on patient to ignore only for this import or permanent
     */
    ignorePatientForImportOnce () {
      if (this.isLoading) return
      this.isLoading = true

      if (this.details?._id) {
        this.$http.put('/import/patient/ignore', {id: this.details._id, type: 'once'})
          .then(() => {
            this.showIgnoreDialog = false
            this.returnToOverview(true)
          })
          .catch(this.NotifyError)
          .finally(() => {
            this.isLoading = false
          })
      }
    },

    /**
     * Set flag on patient to ignore for every import or with duration
     *
     * @param ignoreParams The object with date and type
     */
    ignorePatientForImportWithDuration (ignoreParams) {
      if (this.isLoading) return
      this.isLoading = true

      if (ignoreParams && this.details?._id) {
        let requestData = {
          id: this.details._id,
          type: 'permanent'
        }

        if (ignoreParams.date !== null) {
          requestData.type = 'addUntil'
          requestData.date = ignoreParams.date
        }

        this.$http.put('/import/patient/ignore', requestData)
            .then(() => {
              this.showDurationDialog = false

              this.returnToOverview(true)
            })
            .catch(this.NotifyError)
            .finally(() => {
              this.isLoading = false
            })
      } else {
        this.isLoading = false
        this.showDurationDialog = false
      }
    },

    /**
     * Set background color of form in view in dependency to state
     *
     * @param form The For with its elements
     * @param returnHex If true return the hex-value of color
     */
    setBackgroundColor (form, returnHex) {
      let hexColorDark

      if (!form.isRecent) {
        switch (form.importStatus) {
          case 'ignore':
            hexColorDark = this.compColor.isInDarkMode ? '#444444' : '#dddddd'
            return returnHex ? hexColorDark : 'legend-color-history-grey'
          case 'delete':
            hexColorDark = this.compColor.isInDarkMode ? '#51252f' : '#feebee'
            return returnHex ? hexColorDark : 'legend-color-history-red'
          case 'complete':
            hexColorDark = this.compColor.isInDarkMode ? '#214335' : '#e1ffc9'
            return returnHex ? hexColorDark : 'legend-color-history-green'
          default:
            hexColorDark = this.compColor.isInDarkMode ? '#51552f' : '#fff8e5'
            return returnHex ? hexColorDark : 'legend-color-history-yellow'
        }
      }

      hexColorDark = this.compColor.isInDarkMode ? '#515862' : '#ffffff'
      return returnHex ? hexColorDark : 'legend-color-history-white'
    },

    /**
     * Set Color in list of recent forms in dependency to their state
     */
    getColorOfRecentForm (form) {
      if (form?.formStatus === 'sent') {
        return 'blue'
      }

      if (form?.formStatus === 'ignoredForReport') {
        return 'grey'
      }

      if (form?.formStatus === 'completed') {
        return 'green'
      }

      return 'yellow'
    },

    /**
     * Format display of diagnosis in form
     *
     * @param diagnosis
     * @returns {string}
     */
    formatDiagnosisText (diagnosis) {
      let text = diagnosis.icd10.code

      text += diagnosis.date ? ' (' + this.$options.filters['formatDate'](diagnosis.date, 'DD.MM.YYYY') : ' (kein Datum'

      if (diagnosis.isPairedTumor && diagnosis.localization && this.sideLocalizations.find((item) => diagnosis.localization === item.value)) {
        text += ' - ' + this.sideLocalizations.find((item) => diagnosis.localization === item.value).text
      }

      text += ')'

      return text
    },

    /**
     * Format view of date of systemic therapy in list-view of possible report reasons
     *
     * @param listElement The element of the date list
     */
    formatDateOfSystemicTherapy (listElement) {
      return listElement.date ? this.$options.filters['formatDate'](listElement.date, 'DD.MM.YYYY') : 'kein Datum'
    },

    /**
     * Return String with associated diagnosis-code
     *
     * @param form The form, which is associated to a diagnosis
     */
    getDiagnosisForHistory (form) {
      const diagnosis = this.details.diagnoses.find(elem => {
        return (elem.uuid && elem.uuid === form.diagnosis) || (elem.subjId && elem.subjId === form.subjId)
      })

      return diagnosis && diagnosis.icd10.code ? this.formatDiagnosisText(diagnosis) : 'keine Diagnose'
    },

    /**
     * Set date accuracy and version per default after date input
     */
    setIcdVersionAfterDate (item) {
      if (item.date && moment(item.date, ['DD.MM.YYYY', 'DD.MM.YY', moment.ISO_8601]).isValid()) {
        const dateYear = new Date(item.date).getFullYear()
        const selectedVersion = this.Primaertumor_ICD_VersionTypes.find((code) => { return code.misdValue === dateYear.toString() })

        // version will only be set, if year is part of version-list, else set it on "Sonstige"
        if (selectedVersion) {
          this.$set(item.icd10, 'version', selectedVersion.value)
        } else {
          this.$set(item.icd10, 'version', this.Primaertumor_ICD_VersionTypes[this.Primaertumor_ICD_VersionTypes.length - 1].value)
        }
      }
    },

    /**
     * Get list of icd10 codes via regex in auto-complete
     *
     * @param data The search string
     * @param diagnosis The selected diagnosis element
     * @param index The index of selected diagnosis element
     */
    getICD10ForAutocomplete (data, diagnosis, index) {
      if (data && diagnosis?.icd10?.code && data !== diagnosis.icd10.code) {
        this.$set(diagnosis.icd10, 'description', undefined)
      }

      // clear string from special characters to provide errors
      data = this.clearStringForRegexSearch(data)

      let config = {
        opts: {
          limit: 30
        },
        query: {
          searchValue: data
        }
      }

      // if icd-10 version is set, send it to backend
      const foundVersion = this.Primaertumor_ICD_VersionTypes.find(elem => elem.value === diagnosis.icd10.version)

      if (foundVersion?.misdValue) {
        config.query.version = foundVersion.misdValue
      }

      // set oldest version of misd-service, when "Sonstige" is set
      if (foundVersion?.value === 'Sonstige') {
        config.query.version = this.Primaertumor_ICD_VersionTypes[this.Primaertumor_ICD_VersionTypes.length - 2].misdValue
      }

      // set version "Sonstige" as default, if not exists
      if (!diagnosis.icd10.version) {
        diagnosis.icd10.version = 'Sonstige'
        config.query.version = this.Primaertumor_ICD_VersionTypes[this.Primaertumor_ICD_VersionTypes.length - 2].misdValue
      }

      this.$http.get('/icd10?config=' + JSON.stringify(config) + '&filtered=tumor')
          .then(res => {
            this.resultSearchModel = {
              result: res.data.items,
              index: index,
              count: res.data.count,
              version: diagnosis.icd10.version === 'Sonstige' ? 'Sonstige' : res.data.version
            }
          })
          .catch(this.NotifyError)
    },

    /**
     * Set icd10 value via list form auto complete
     *
     * @param data The selected element from list in auto-complete
     * @param diagnosis The selected diagnosis
     * @param index The index of element in diagnosis array
     */
    setDiagnosis (data, diagnosis, index) {
      if (data && data.code) {
        const foundVersion = this.Primaertumor_ICD_VersionTypes.find(elem => elem.misdValue === this.resultSearchModel.version)
        const version = foundVersion?.value ? foundVersion?.value : this.formsMetaData.ICD10_Version

        this.$set(diagnosis.icd10, 'code', data.code)
        this.$set(diagnosis.icd10, 'description', data.description)
        this.$set(diagnosis.icd10, 'version', version)

        // set ICDO after changing ICD10
        this.setImportICDOAfterICD10(data, diagnosis)
      } else {
        if (!diagnosis.icd10.code || typeof data === 'string' || (this.$refs['ImportDetailForm'].errors  &&  this.$refs['ImportDetailForm'].errors['diagnoses.' + index + '.icd10.code'] && this.$refs['ImportDetailForm'].errors['diagnoses.' + index + '.icd10.code'].length > 0)) {
          // nextTick is necessary to trigger watcher in autocomplete on the right way
          this.$nextTick(() => {
            // delete input of icd10, if not selected from result list
            if (typeof data === 'string') this.$set(diagnosis.icd10, 'code', null)
          })

          this.$set(diagnosis.icd10, 'description', undefined)
        }
      }
    },

    /**
     * Set ICDO-Code after ICD10
     */
    setImportICDOAfterICD10 (icd10, diagnosis) {
      // icd10 code must be set and must be a c-diagnosis up to 8 on first segment
      const isAssignableCode = icd10 && icd10.code && icd10.code.match(/^C[0-7]|C80/)

      let code = ''

      // set icd-o only if not exists and icd10 has the right match
      if (isAssignableCode) {
        const codeLength = icd10 && icd10.code && icd10.code.length

        if (codeLength === 3) {
          code = icd10.code + '.9'
        } else if (codeLength === 5) {
          code = icd10.code
        }
      }

      if (icd10 && icd10.code && this.PairedAdditionalTumors[icd10.code]) {
        code = this.PairedAdditionalTumors[icd10.code]
      }

      if (icd10 && icd10.code && this.NotPairedAdditionalTumors[icd10.code]) {
        code = this.NotPairedAdditionalTumors[icd10.code]
      }

      // set icd-o if code exists or delete content of icd-o
      if (code) {
        let config = {
          opts: {
            limit: 30
          },
          query: {
            searchValue: code
          }
        }

        // if icd-10 version is set, send it to backend
        const foundVersion = this.Primaertumor_ICD_O_VersionTypes.find(elem => elem.value === diagnosis.icdO?.version)

        if (foundVersion?.misdValue) {
          config.query.version = foundVersion.misdValue
        }

        this.$http.get('/icdO?config=' + JSON.stringify(config))
            .then(res => {
              // create icdO object if not exists to prevent errors while trying to set code
              if (!diagnosis.icdO) {
                this.$set(diagnosis, 'icdO', {})
              }

              if (res?.data?.items && res.data.items.length === 1) {
                this.$set(diagnosis.icdO, 'code', res.data.items[0].code)
                this.$set(diagnosis.icdO, 'description', res.data.items[0].description)
                this.$set(diagnosis.icdO, 'version', diagnosis.icdO?.version || this.formsMetaData.ICDO_Version)
              } else {
                this.$set(diagnosis.icdO, 'code', code)
                this.$set(diagnosis.icdO, 'description', icd10.description)
                this.$set(diagnosis.icdO, 'version', diagnosis.icdO?.version || this.formsMetaData.ICDO_Version)
              }
            })
            .catch(this.NotifyError)
      } else {
        // delete only code if icdO object exists
        if (diagnosis.icdO) {
          this.$delete(diagnosis, 'icdO')
        }
      }
    },

    /**
     * Reset icd10-code of selected diagnosis after changing version
     *
     * @param diagnosis The diagnosis.icd10 object
     */
    resetICD10Code (diagnosis) {
      this.$delete(diagnosis, 'code')
      this.$delete(diagnosis, 'description')
    },

    /**
     * Check if report reason is valid to disable/enable buttons
     *
     * @param ref The validation ref to check header and content
     * @returns {boolean|*}
     */
    formIsInValid (ref) {
      if (Array.isArray(this.$refs[ref + '.header']) && this.$refs[ref + '.header'][0] && this.$refs[ref + '.header'][0].flags) {
        return this.$refs[ref + '.header'][0].flags.invalid || this.$refs[ref + '.content'] && this.$refs[ref + '.content'][0] && this.$refs[ref + '.content'][0].flags.invalid
      } else if (this.$refs[ref + '.header'] && this.$refs[ref + '.header'].flags) {
        return this.$refs[ref + '.header'].flags.invalid || this.$refs[ref + '.content'] && this.$refs[ref + '.content'] && this.$refs[ref + '.content'].flags.invalid
      }

      return false
    },

    /**
     * Check if header of report reason is valid to disable/enable buttons
     *
     * @param ref The validation ref to check header and content
     * @returns {boolean|*}
     */
    formHeaderIsInValid (ref) {
      if (Array.isArray(this.$refs[ref + '.header']) && this.$refs[ref + '.header'][0] && this.$refs[ref + '.header'][0].flags) {
        return this.$refs[ref + '.header'][0].flags.invalid
      } else if (this.$refs[ref + '.header'] && this.$refs[ref + '.header'].flags) {
        return this.$refs[ref + '.header'].flags.invalid
      }

      return false
    },

    /**
     * Check if one of selected forms to import is invalid
     *
     * @returns {boolean}
     */
    areImportFormsInvalid () {
      return Object.keys(this.$refs.ImportDetailForm?.fields || {}).some(key => {
        // split validation field name in its elements
        const keySplit = key.split('.')

        // check if element ist part of forms or newForm
        if (keySplit && (keySplit[0] === 'forms' || keySplit[0] === 'diagnoses' || keySplit[0] === 'newAdditionalForm')) {

          const form = keySplit[0] !== 'newAdditionalForm' ? this.details[keySplit[0]][keySplit[1]] : this[keySplit[0]]
          return form && form.isSelected ? this.$refs.ImportDetailForm.fields[key] && this.$refs.ImportDetailForm.fields[key].invalid : false
        }
      })
    },

    /**
     * Check if base data of patients are invalid
     *
     * @returns {*}
     */
    baseDataAreInvalid () {
      const headerInvalid = this.$refs.baseDataHeader && this.$refs.baseDataHeader.flags && this.$refs.baseDataHeader.flags.invalid
      const bodyInvalid = this.$refs.baseData && this.$refs.baseData.flags && this.$refs.baseData.flags.invalid

      return headerInvalid || bodyInvalid
    }
  },
  computed: {
    atLeastOneDiagnosisIsSelected () {
      return (this.details.recentForms || []).some(form => form.formType === 'register' || (form.formType === 'diagnosis' && form.importStatus && form.importStatus !=='delete'))
    },
    atLeastOneFormIsReadyToImport () {
      return (this.details.recentForms || []).some(form => !form.isRecent)
    },
    isMedicalCommunity () {
      const user = this.authStore.getters.getAuthUserData && this.authStore.getters.getAuthUserData.user ? this.authStore.getters.getAuthUserData.user : {}
      return user && user.cancer_form_details && user.cancer_form_details.isMedicalCommunity
    },
    baseDataHasChanges () {
      let baseDataHeaderIsDirty = false
      let baseDataIsDirty = false

      if (this.$refs?.baseDataHeader?.fields) {
        baseDataHeaderIsDirty = Object.keys(this.$refs.baseDataHeader.fields).some(field => this.$refs.baseDataHeader.fields[field].changed)
      }

      if (this.$refs?.baseData?.fields) {
        baseDataIsDirty = Object.keys(this.$refs.baseData.fields).some(field => this.$refs.baseData.fields[field].changed)
      }
      return this.showBaseData && (baseDataHeaderIsDirty || baseDataIsDirty)
    },
    physicianList () {
      const user = this.authStore.getters.getAuthUserData && this.authStore.getters.getAuthUserData.user ? this.authStore.getters.getAuthUserData.user : {}
      let physicianArray = []

      if (user && user.cancer_form_details && user.cancer_form_details.medicalCommunity && user.cancer_form_details.medicalCommunity.physicians) {
        user.cancer_form_details.medicalCommunity.physicians.forEach(physician => {
          physicianArray.push({
            value: {
              name: physician.name,
              melderId: physician.melderId,
              lanr: physician.lanr,
              iknr: physician.iknr
            }
          })
        })
      }

      return physicianArray
    },
    dateSelection () {
      let dateArray = []

      if (this.details && this.details.eventDates) {
        // the date of all forms except diagnoses must be later than the oldest diagnosis
        const firstDiagnosis = this.details.diagnoses[0]

        this.details.eventDates.forEach(date => {
          const returnValueFirstDiagnosis = firstDiagnosis && firstDiagnosis.date ? moment(date).isAfter(firstDiagnosis.date, 'day') || moment(date).isSame(firstDiagnosis.date, 'day') : true
          const returnValueStart = this.exportPeriodFrom && moment(this.exportPeriodFrom).isValid() ? moment(date).isAfter(this.exportPeriodFrom, 'day') || moment(date).isSame(this.exportPeriodFrom, 'day') : true

          if (returnValueStart && returnValueFirstDiagnosis) {
            dateArray.push(date)
          }
        })
      }

      return dateArray
    },
  },

  watch: {
    reloadTrigger () {
      this.getData()
    }
  }
}
</script>

<style>
.history_is_disabled {
  color: rgba(0, 0, 0, 0.38);
}

.history_is_disabled_dark {
  color: rgba(255, 255, 255, 0.5)
}

.lx-button-group .lx-btn:first-child,
.lx-button-group .lx-btn:first-child:before {
  border-radius: 6px 0 0 6px !important;
}

.lx-button-group .lx-btn:last-child,
.lx-button-group .lx-btn:last-child:before {
  border-radius: 0 6px 6px 0 !important;
}

.lx-button-group .lx-btn.v-btn:before {
  border-color: #192330 !important;
  color: #192330 !important;
  caret-color: #192330 !important;
}

.label_no_wrap {
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
</style>
